import { createFileRoute } from "@tanstack/react-router";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

type ReleaseEntry = {
  id: string;
  timestamp: string;
  title: string;
  summary: string;
  userHighlights: string[];
  technicalNotes: string[];
};

const releaseEntries: ReleaseEntry[] = [
  {
    id: "generated-text-page-loop-fixes-and-e2e-smoke-pass",
    timestamp: "2026-03-15T09:15:13+01:00",
    title: "Fixed reeprise page render loops and verified generated-text creation end to end",
    summary:
      "The reeprise home page and generated-text details page no longer fall into React update-depth loops during text creation, and the create-text flow is now verified with a passing Playwright smoke test.",
    userHighlights: [
      "The main text page no longer gets stuck in a render loop before submission.",
      "Opening a generated text detail page no longer retriggers the old vocabulary-selection loop.",
      "The critical flow from creating a text to opening its completed details page now passes as an automated smoke check."
    ],
    technicalNotes: [
      "Made `GenerateTextCard` form default hydration idempotent by hydrating once per defaults snapshot instead of reapplying values on every render.",
      "Guarded the empty-state reset logic in `VocabularyPreviewCard` so it does not keep resetting already-empty state on every render when no vocabulary is available.",
      "Guarded `selectedVocabularyIds` synchronization in `$generatedTextId.tsx` and tightened the new Playwright smoke test to seed valid create-form defaults and submit the correct form."
    ]
  },
  {
    id: "generated-text-smoke-test-and-critical-flow-policy",
    timestamp: "2026-03-15T08:51:36+01:00",
    title: "Generated text creation now has a dedicated smoke test and repo policy",
    summary:
      "The repo now treats generated-text creation as a critical flow that must be verified with an executed regression test, helping catch broken background generation earlier during development.",
    userHighlights: [
      "The most important text-generation flow now has automated smoke coverage.",
      "Development checks are now expected to verify real generation behavior, not just builds.",
      "Broken worker or Redis setups should be caught earlier before UI changes are considered done."
    ],
    technicalNotes: [
      "Added a Playwright smoke test that logs in, creates a generated text, polls the generated-text API until completion, and opens the details page.",
      "Updated `AGENTS.md` with a critical-flow testing policy for generated-text creation and a required first-pass check of Django, Celery, Beat, Redis, and text status when generation is stuck.",
      "This change is intended to catch failures like `PENDING` texts caused by unavailable background workers."
    ]
  },
  {
    id: "toast-duration-standardized-two-seconds",
    timestamp: "2026-03-15T08:06:41+01:00",
    title: "Standardized toast timing to a shorter transient duration",
    summary:
      "The app toast now uses a standard short success duration again, so brief confirmations disappear faster and feel closer to common UX patterns.",
    userHighlights: [
      "Short confirmations like `Word added` now fade out faster.",
      "The toast still remains visible long enough to read at a glance.",
      "This change only affects timing, not toast styling or placement."
    ],
    technicalNotes: [
      "Reduced the `SnackBar.tsx` auto-close timer from 2600ms to 2000ms.",
      "This keeps the toast aligned with common transient success-toast timing."
    ]
  },
  {
    id: "agents-german-umlaut-copy-rule",
    timestamp: "2026-03-15T08:03:53+01:00",
    title: "Added a repo rule to always use real German umlauts in copy",
    summary:
      "The repository instructions now explicitly require real German characters in user-facing text, so copy should use `ü`, `ö`, `ä`, and `ß` whenever the correct spelling needs them.",
    userHighlights: [
      "German UI copy should now consistently use proper spellings like `hinzugefügt`.",
      "ASCII fallback forms like `ue`, `oe`, `ae`, or `ss` are now explicitly discouraged when the proper character is required.",
      "This is an instruction change for future edits rather than a product behavior change."
    ],
    technicalNotes: [
      "Updated `AGENTS.md` with a dedicated German copy rule for umlauts and `ß`.",
      "The rule applies to future user-facing text changes across the repo."
    ]
  },
  {
    id: "word-added-toast-german-umlaut-fix",
    timestamp: "2026-03-15T08:02:42+01:00",
    title: "Corrected the German `Word added` toast wording",
    summary:
      "The German vocabulary-success toast now uses the proper umlaut spelling in `Wort hinzugefügt` instead of the ASCII fallback.",
    userHighlights: [
      "German toast copy now reads `Wort hinzugefügt`.",
      "The English toast stays `Word added`.",
      "This only changes wording, not toast behavior."
    ],
    technicalNotes: [
      "Updated the `word_added_toast` German translation value in `translations.json`.",
      "No component logic or toast timing changed in this adjustment."
    ]
  },
  {
    id: "generated-text-title-highlight-no-outline-ring",
    timestamp: "2026-03-15T08:00:03+01:00",
    title: "Title-word highlights now use the same fill-only style as body text",
    summary:
      "Highlighted vocabulary in generated text titles now matches the main reading text exactly, with no extra outline ring added on headline clicks.",
    userHighlights: [
      "Headline words now highlight with the same simple green fill as words in the story body.",
      "Clicking a title word no longer adds an extra outline stroke around that word.",
      "The title and body now feel visually identical when showing saved vocabulary."
    ],
    technicalNotes: [
      "Removed the title-only flash ring state from `GeneratedTextDetailsTextPane`.",
      "Title words now keep only the persistent fill highlight, matching the saved-vocabulary treatment used in the main text.",
      "No backend changes."
    ]
  },
  {
    id: "generated-text-title-click-no-first-body-word-highlight",
    timestamp: "2026-03-15T07:48:55+01:00",
    title: "Title clicks no longer highlight the first word in the body text",
    summary:
      "Adding vocabulary from a generated text headline no longer leaks into positional body highlights, so clicking any headline word keeps the effect scoped to the headline.",
    userHighlights: [
      "Clicking the first word in a title no longer highlights the first word of the story.",
      "Headline vocabulary still gets added correctly and keeps its headline highlight.",
      "Body highlights now only come from words that were actually added from the body text."
    ],
    technicalNotes: [
      "Filtered title-origin vocabulary entries out of `usages`-based body highlight positions in `GeneratedTextDetailsTextPane`.",
      "Used the resolved title sentence as the boundary for deciding whether a saved vocabulary belongs to the headline or the reading body.",
      "Applied the same exclusion in both chapter and non-chapter text rendering paths."
    ]
  },
  {
    id: "generated-text-title-highlights-persist-after-refresh",
    timestamp: "2026-03-15T07:47:47+01:00",
    title: "Title-word highlights now persist after page refresh",
    summary:
      "Words added from a generated text title now stay highlighted after reloading the page, matching the persistence users already expect from saved vocabulary inside the body text.",
    userHighlights: [
      "Words you added from the title stay visibly highlighted after a refresh.",
      "The title keeps the same saved-vocabulary visual state as the text body.",
      "Refreshing no longer makes title-added words look as if they were never added."
    ],
    technicalNotes: [
      "Rebuilt title highlights in `GeneratedTextDetailsTextPane` from saved vocabulary records instead of relying only on local click state.",
      "Matched title-origin vocabulary by comparing saved `sentence` values against the resolved generated title.",
      "Also restored the persisted `title-only` classification in the details route so refreshes do not reintroduce body-highlight side effects."
    ]
  },
  {
    id: "generated-text-title-highlight-style-matched",
    timestamp: "2026-03-14T08:24:26+01:00",
    title: "Title-word highlights now match text-word highlights",
    summary:
      "Words added from the generated text title now use the same green highlight treatment as added vocabulary inside the main text, so both interaction zones feel consistent.",
    userHighlights: [
      "Added vocabulary highlights now look the same in the title and the text body.",
      "Title clicks still behave the same and remain title-local.",
      "The green highlight fill no longer looks visually different just because the word was clicked in the title."
    ],
    technicalNotes: [
      "Adjusted title-word button classes in `GeneratedTextDetailsTextPane.tsx` to use the same conditional background logic as body highlight spans.",
      "Stopped forcing `bg-transparent` in highlighted title states so the green fill can render identically.",
      "No backend changes."
    ]
  },
  {
    id: "word-added-toast-shared-parent-handler",
    timestamp: "2026-03-14T08:18:56+01:00",
    title: "`Word added` toast now fires from the shared add-vocabulary handler",
    summary:
      "The vocabulary success toast is now triggered from the common parent callback instead of the individual title/text click handlers, so `Word added` appears consistently no matter where the user taps.",
    userHighlights: [
      "Tapping a word in the title still shows `Word added`.",
      "Tapping a word in the body text now shows the same `Word added` toast.",
      "The toast behavior is now consistent across both add-word entry points."
    ],
    technicalNotes: [
      "Removed direct snackbar calls from the title/body add-word code paths inside `GeneratedTextDetailsTextPane.tsx`.",
      "Moved the toast trigger into the shared `onVocabularyCreate(...)` handler in `reeprise/$generatedTextId.tsx`.",
      "This centralizes add-word feedback behind one callback so title and text interactions cannot drift apart."
    ]
  },
  {
    id: "generated-text-title-click-no-body-mis-highlight",
    timestamp: "2026-03-14T08:17:58+01:00",
    title: "Title clicks now stop causing wrong body-word highlights",
    summary:
      "Adding vocabulary from the generated text title now keeps the title interaction local, so the reading area no longer highlights an unrelated body word after a title click.",
    userHighlights: [
      "Clicking a title word still adds that word to your vocabulary list.",
      "The clicked title word keeps its title highlight behavior.",
      "The main text no longer highlights a mismatched body word after a title-based vocabulary add."
    ],
    technicalNotes: [
      "Split generated-text vocabulary creation events by source: `title` versus `text`.",
      "Updated the details page to exclude title-origin vocabulary IDs from body-word highlight derivation.",
      "Body highlights still respond normally for vocabulary created from direct text-span clicks."
    ]
  },
  {
    id: "word-added-toast-mobile-visibility-tune",
    timestamp: "2026-03-14T08:15:30+01:00",
    title: "Compact `Word added` toast now sits higher and stays visible longer",
    summary:
      "The compact vocabulary-success toast is now positioned near the top on mobile and remains visible a bit longer, so the short `Word added` confirmation is easier to notice.",
    userHighlights: [
      "On mobile, `Word added` now appears near the top-center instead of low in the screen.",
      "The toast still stays compact with no extra helper text.",
      "The message remains visible slightly longer before fading out."
    ],
    technicalNotes: [
      "Updated `SnackBar.tsx` to anchor the snackbar near the top on mobile while keeping the lower-right placement on larger screens.",
      "Removed the width-constraining wrapper behavior and switched the message pill to a natural-content layout.",
      "Extended the auto-close timer from 2000ms to 2600ms to improve visibility for very short success messages."
    ]
  },
  {
    id: "compact-word-added-toast",
    timestamp: "2026-03-14T08:11:24+01:00",
    title: "Vocabulary add feedback is now a compact `Word added` toast",
    summary:
      "Adding a vocabulary word from generated text now shows a much smaller success toast with only the final confirmation message, instead of a larger styled card with extra labels.",
    userHighlights: [
      "The toast is now compact and only says `Word added`.",
      "It appears after the word was actually added, not before the request finishes.",
      "Mobile still keeps the toast above the sticky bottom action area."
    ],
    technicalNotes: [
      "Simplified `SnackBar.tsx` into a small rounded weather-tinted pill while preserving the mobile-safe placement and z-index.",
      "Replaced the previous pre-request `Generating Vocabulary.` notifications in `GeneratedTextDetailsTextPane.tsx` with a post-success `word_added_toast` notification.",
      "Removed the now-unused secondary snackbar translation keys and added `word_added_toast` to `translations.json`."
    ]
  },
  {
    id: "snackbar-matched-daily-card-layout",
    timestamp: "2026-03-14T08:08:01+01:00",
    title: "Success toast now matches the daily card layout more closely",
    summary:
      "The success toast now follows the same wide sky-card composition as the daily text card, with capsule status copy, serif message styling, and integrated cloud shapes.",
    userHighlights: [
      "The toast now looks like a compact version of the daily card instead of a separate widget style.",
      "Status feedback is reduced to a simple `All worked` capsule and the actual action message.",
      "Mobile still keeps the toast above the sticky bottom action area."
    ],
    technicalNotes: [
      "Reworked `SnackBar.tsx` from a centered badge layout into a wide weather-card panel with left status pill, serif content, and right-side timestamp accent.",
      "Adjusted cloud placement and overlay treatment to mirror the daily card structure more directly.",
      "Added translation key `snackbar_now` for the small time accent label."
    ]
  },
  {
    id: "weather-card-inspired-success-toast",
    timestamp: "2026-03-14T08:02:41+01:00",
    title: "Success toasts now borrow the daily weather-card look",
    summary:
      "Feedback toasts now use the same bright-sky and cloud language as the daily text card, with a simpler success message and mobile-safe placement above sticky actions.",
    userHighlights: [
      "Toasts now feel visually connected to the daily text card instead of using a separate brand-style bubble.",
      "The toast no longer shows `Uxhulu`; it just confirms the action in a cleaner success layout.",
      "On mobile, the toast still stays above the fixed bottom action area so the message remains readable."
    ],
    technicalNotes: [
      "Updated `SnackBar.tsx` to use a weather-card-inspired sky gradient, cloud clusters, and a compact success badge with check icon.",
      "Removed the extra brand label and replaced it with a minimal `All worked` confirmation line under the main message.",
      "Kept the snackbar at `z-40` with safe-area-aware bottom spacing so it clears the mobile chapter-generation CTA."
    ]
  },
  {
    id: "generated-text-details-story-stream-restored",
    timestamp: "2026-03-14T08:01:23+01:00",
    title: "Generated text details now stream story text again without JSON flashes",
    summary:
      "The generated text details page now keeps the safe header/body phases and restores the live reading effect by streaming partial story text directly from the structured `story` field.",
    userHighlights: [
      "Story text now grows live again while generation is running.",
      "The main title still stays on `Titel wird erstellt …` until a real title is ready.",
      "The reading area still never shows raw JSON syntax or `title` payload text."
    ],
    technicalNotes: [
      "Adjusted the generated-text resolver to allow partial extraction of the structured `story` string while a payload is still incomplete.",
      "Kept title parsing strict so incomplete title transport data does not leak into the header.",
      "Safe streaming still hides raw JSON framing and only renders decoded story characters."
    ]
  },
  {
    id: "cloud-toast-snackbar-refresh",
    timestamp: "2026-03-14T07:55:33+01:00",
    title: "Toasts now float in a cloud-style bubble above mobile actions",
    summary:
      "Feedback toasts now use a softer cloud-like design and sit above sticky mobile controls, so status messages stay visible without clashing with the reading layout.",
    userHighlights: [
      "Toast messages now appear in a rounded cloud-style bubble that matches the app's lighter editorial look.",
      "On mobile, the toast now clears the sticky bottom action area instead of getting hidden behind it.",
      "The updated toast stays centered on small screens and moves back to the lower-right corner on larger screens."
    ],
    technicalNotes: [
      "Redesigned `SnackBar.tsx` with layered circular shapes and a light sky-tinted gradient instead of the previous plain rectangular slab.",
      "Raised the snackbar stacking order to `z-40` and added mobile-safe bottom padding so it clears the fixed chapter-generation CTA.",
      "Removed shadow-based styling from the toast refresh to stay aligned with repository frontend guidance."
    ]
  },
  {
    id: "generated-text-details-explicit-stream-phases",
    timestamp: "2026-03-14T07:49:22+01:00",
    title: "Generated text details now use explicit safe streaming phases",
    summary:
      "The generated text details page now renders generation through explicit title/story phases so the header no longer falls back to the topic and the body never shows raw JSON transport data.",
    userHighlights: [
      "While a title is not ready yet, the page now keeps the neutral `Titel wird erstellt …` header instead of showing the typed topic as the main title.",
      "The body stays empty until a clean story is available, then streams only story text.",
      "JSON `title` and `story` payload text no longer flashes inside the reading area during generation."
    ],
    technicalNotes: [
      "Added a shared `resolveGeneratedTextContent(...)` helper with explicit phases: `pending_no_title`, `pending_title_only`, `pending_story`, and `complete`.",
      "Updated `GeneratedTextDetailsTextPane` to resolve one header state and one body state instead of independently mixing topic, websocket chunks, and final saved output.",
      "Pending-state story rendering now reads only a closed `story` string field from structured payloads; otherwise it renders nothing until the content is safe."
    ]
  },
  {
    id: "generated-text-details-stable-generation-copy",
    timestamp: "2026-03-14T07:18:36+01:00",
    title: "Generated text details now hide raw JSON and keep a stable title while loading",
    summary:
      "The generated text details page now uses the same loading copy as the text cards, never exposes raw JSON during streaming, and transitions smoothly into the finished text.",
    userHighlights: [
      "The details header now shows `Titel wird erstellt …` until a real generated title is available.",
      "Users no longer see raw JSON or code fences flash in the main text while generation is still in progress.",
      "The loading-to-finished transition now keeps the same title/body rendering path for a smoother read."
    ],
    technicalNotes: [
      "Extracted shared generated-text parsing helpers so cards and the details page resolve AI title and story content the same way.",
      "Updated the details page to derive its title from parsed AI output instead of the generic prompt topic during pending state.",
      "Suppressed incomplete JSON/code-fence payloads in streamed text parsing so only valid story content is rendered."
    ]
  },
  {
    id: "daily-and-tricky-text-origins-separated",
    timestamp: "2026-03-14T06:37:56+01:00",
    title: "Daily texts and tricky-word texts now stay separate",
    summary:
      "The daily learning card now creates its own daily text instead of reusing the tricky-word practice flow, so both cards can support different learning goals.",
    userHighlights: [
      "The daily card now opens a separate daily learning text just for that nudge.",
      "The tricky-word card keeps its own practice texts focused on hard words.",
      "Reading one card no longer marks the other card as already generated."
    ],
    technicalNotes: [
      "Added explicit `GeneratedText.origin` values so the app can distinguish manual, daily, and tricky-word texts without guessing from content.",
      "Changed `/generated_texts/daily-practice/` to create `DAILY_NUDGE` texts that use recent interests and vocabulary context instead of the struggle-word generator.",
      "Updated `DailyPracticeCallout` to filter daily-card history by origin and added backend regression tests for same-day separation."
    ]
  },
  {
    id: "daily-callout-keep-all-generated-daily-cards-neutral-history",
    timestamp: "2026-03-03T21:09:56+01:00",
    title: "Daily callout now keeps all generated daily cards as neutral history",
    summary:
      "All generated daily cards now remain visible in the top daily section, while only one main card keeps the weather-driven style and the rest are neutral compact history cards.",
    userHighlights: [
      "Older generated daily cards no longer disappear from the daily section.",
      "Only one main card uses the full weather/daylight visual treatment.",
      "All other daily cards are shown in compact neutral style and stay clickable."
    ],
    technicalNotes: [
      "Reworked `DailyPracticeCallout` to build a sorted list of all daily-practice texts and render non-featured items in a neutral compact list.",
      "Introduced `featuredDailyTextId` to ensure exactly one weather-styled main card at a time.",
      "Updated `dailyTextPreviousBadge` copy for multi-item history context."
    ]
  },
  {
    id: "daily-callout-keep-yesterday-minimal-card",
    timestamp: "2026-03-03T21:04:02+01:00",
    title: "Restructured daily callout to keep yesterday's card in minimal mode",
    summary:
      "When no new daily text is done yet today, yesterday's daily text now remains visible as a compact neutral card below the active daily card.",
    userHighlights: [
      "Yesterday's daily text no longer disappears from the top area.",
      "The previous card is intentionally compact and neutral.",
      "You can still open yesterday's text directly from this minimal card."
    ],
    technicalNotes: [
      "Added previous-day daily-text detection in `DailyPracticeCallout` using dated daily-practice heuristics.",
      "Rendered a new compact neutral card branch for the latest pre-today daily text when today is not done.",
      "Added translation key `dailyTextPreviousBadge` for the compact card label."
    ]
  },
  {
    id: "directional-srs-vocabulary-trainer",
    timestamp: "2026-03-02T20:49:20+01:00",
    title: "Vocabulary trainer now tracks SRS in both directions",
    summary:
      "The trainer now stores separate spaced-repetition progress for Target→Native and Native→Target, and introduces reverse cards only after forward progress is stable.",
    userHighlights: [
      "Your answers now count for the exact card direction you practiced.",
      "Reverse direction (Native→Target) appears gradually after you build a solid forward streak.",
      "Trainer cards now show a clear direction label so each prompt is unambiguous."
    ],
    technicalNotes: [
      "Backend: `VocabularyUserProgress` now stores `direction`; review actions persist streak/last_tested per direction; trainer state now stores active `review_direction`.",
      "Trainer queue logic now selects due cards direction-aware, unlocks reverse cards after a forward streak threshold, and returns per-card `review_direction`.",
      "Frontend trainer sends `review_direction` in review mutations and renders headers/subheaders from per-card direction data."
    ]
  },
  {
    id: "weather-location-smart-search-dropdown",
    timestamp: "2026-03-02T20:38:15+01:00",
    title: "Added smart weather-location search with dropdown suggestions",
    summary:
      "Settings now provides city autocomplete via backend geocoding suggestions, so users can pick valid locations faster and auto-fill country code.",
    userHighlights: [
      "Typing at least 2 characters in the city field now opens a location dropdown.",
      "Selecting a suggestion fills both city and country code automatically.",
      "Supports common variants like Vienna/Wien through geocoding-backed search."
    ],
    technicalNotes: [
      "Backend: added authenticated `GET /api/reeprise/weather/location-suggestions/` endpoint with Open-Meteo geocoding, optional country filter, fallback, and caching.",
      "Frontend: added `useWeatherLocationSuggestions` hook and integrated dropdown UI in `settings.tsx`.",
      "Added i18n keys for suggestion loading and empty states in `translations.json`."
    ]
  },
  {
    id: "settings-weather-location-controls-ui",
    timestamp: "2026-03-02T20:29:29+01:00",
    title: "Added weather location controls in Settings",
    summary:
      "Users can now set city and country code in Settings so the daily card weather theme uses their chosen location.",
    userHighlights: [
      "New settings section for daily-card weather location.",
      "Save city and 2-letter country code directly from the app.",
      "Inline saving, success, and error feedback for location updates."
    ],
    technicalNotes: [
      "Updated `settings.tsx` with controlled `weather_city` and `weather_country_code` inputs wired to `useUserSettingsUpdate`.",
      "Added new translation keys in `translations.json` for weather-location labels, hints, and status messages.",
      "Country code input is normalized to uppercase and constrained to 2 characters before PATCH."
    ]
  },
  {
    id: "daily-done-style-today-vs-past-scope-fix",
    timestamp: "2026-03-02T19:24:50+01:00",
    title: "Scoped done-card styling by day (today keeps weather, past goes neutral)",
    summary:
      "The done-state daily card now keeps the weather/daylight look on the same day, and only falls back to neutral styling once it is no longer today.",
    userHighlights: [
      "Heute-erledigt still matches today’s daily weather design.",
      "Only non-current daily cards lose weather styling and appear neutral.",
      "The active new daily card remains the only weather-driven daily prompt each day."
    ],
    technicalNotes: [
      "In `DailyPracticeCallout`, added `isDoneCardForToday` based on `doneCardText.created_at` and local done marker date.",
      "Done-card theme now conditionally uses `activeThemeClasses` for today and neutral classes for non-today states."
    ]
  },
  {
    id: "daily-done-card-no-live-weather-theme",
    timestamp: "2026-03-02T19:22:50+01:00",
    title: "Done-state daily card no longer uses live weather styling",
    summary:
      "The completed daily card now keeps a fixed neutral style, while only the active daily card reflects current weather/daylight mood.",
    userHighlights: [
      "Heute-erledigt card now looks consistent and no longer changes with hourly weather.",
      "Only the active daily prompt card uses live weather visualization.",
      "Avoids the impression that past daily cards all carry changing weather moods."
    ],
    technicalNotes: [
      "In `DailyPracticeCallout`, split theme mapping into `activeThemeClasses` (weather/daylight-aware) and `doneThemeClasses` (fixed neutral/day).",
      "Applied neutral theme classes to the done-card render branch in `reeprise/index.tsx`."
    ]
  },
  {
    id: "daily-done-card-title-preview-by-marker-id-fix",
    timestamp: "2026-03-02T19:19:30+01:00",
    title: "Fixed done-card fallback copy when daily text already exists",
    summary:
      "The done-state daily card now resolves title and preview text via the stored daily text id, preventing fallback copy from showing after the text was generated.",
    userHighlights: [
      "Done-card now more reliably shows the real generated title.",
      "Done-card preview now more reliably shows the beginning of the generated text with `...` when needed.",
      "Fallback `Morgen wartet ...` text is now only used when the actual text record is not available yet."
    ],
    technicalNotes: [
      "Updated `DailyPracticeCallout` in `reeprise/index.tsx` to prioritize `openTextId` lookup in `generatedTexts` before topic-based daily-text heuristics.",
      "This avoids false negatives when the backend replaces the generic daily topic with the final generated title."
    ]
  },
  {
    id: "daily-card-daylight-phase-mood-visualization",
    timestamp: "2026-03-02T19:11:03+01:00",
    title: "Daily card now reflects daylight mood (dawn/day/dusk/night)",
    summary:
      "The weather-adaptive daily card now also adjusts atmosphere by local daylight phase, so visuals match sunrise, daytime, sunset, and night mood.",
    userHighlights: [
      "The same weather theme now looks different in dawn, day, dusk, and night.",
      "Morning card mood is warmer at sunrise, calmer at dusk, and darker at night.",
      "Done-state and active daily card both use the same daylight-aware visual behavior."
    ],
    technicalNotes: [
      "Backend weather-theme endpoint now includes `daylight_phase` derived from local current time and sunrise/sunset from Open-Meteo daily data.",
      "Frontend `useDailyWeatherTheme` DTO extended with `daylight_phase` typing and consumed in `DailyPracticeCallout`.",
      "Updated daily-card class mapper in `reeprise/index.tsx` to combine weather theme layers with daylight-phase mood overlays."
    ]
  },
  {
    id: "weather-adaptive-daily-card-v1-implementation",
    timestamp: "2026-03-02T18:59:27+01:00",
    title: "Implemented weather-adaptive daily card v1 (city-based, hourly refresh)",
    summary:
      "The daily card now adapts its visual mood to local weather using a backend weather-theme endpoint, with Vienna fallback and hourly client refresh.",
    userHighlights: [
      "Daily card now switches among clear/cloudy/rain/snow/night/neutral visual variants while keeping the same core card design language.",
      "Weather theme refreshes every hour and when the page regains focus.",
      "Daily card is now visible from morning hours (from 06:00 local time) instead of only later in the day."
    ],
    technicalNotes: [
      "Backend: added `GET /api/reeprise/generated_texts/weather-theme/` using Open-Meteo via server-side proxy with caching and graceful neutral fallback.",
      "Backend: added `UserSetting` fields `weather_city` and `weather_country_code` (migration `0073_usersetting_weather_location`) and exposed them via `UserSettingSerializer`.",
      "Frontend: added `useDailyWeatherTheme` hook with hourly/focus refetch and integrated theme class mapping into `DailyPracticeCallout` while preserving existing layout/animation language."
    ]
  },
  {
    id: "weather-planning-morning-visibility-requirement",
    timestamp: "2026-03-02T18:43:46+01:00",
    title: "Weather card planning now requires morning visibility",
    summary:
      "The planning now explicitly states that the daily card must be visible in the morning in the user’s local timezone.",
    userHighlights: [
      "Daily card availability is now clearly defined for morning usage.",
      "Timezone handling is explicitly required for visibility timing.",
      "Weather theming must not block or delay morning card display."
    ],
    technicalNotes: [
      "Updated `planning_daily_weather_card.md` scope and added `Visibility Timing` section.",
      "No runtime code logic changed in this update."
    ]
  },
  {
    id: "weather-planning-animation-style-guardrails-added",
    timestamp: "2026-03-02T18:42:01+01:00",
    title: "Weather planning updated with animation consistency guardrails",
    summary:
      "The weather-adaptive card plan now explicitly requires animation style consistency, using the current cloud drift motion as baseline across weather themes.",
    userHighlights: [
      "Ensures weather variants feel like one coherent design system in motion.",
      "Prevents flashy or mismatched animation styles between weather states.",
      "Keeps reduced-motion accessibility behavior consistent across variants."
    ],
    technicalNotes: [
      "Added `Animation Consistency Guardrails` section to `planning_daily_weather_card.md`.",
      "Defined baseline cloud animation approach and weather-specific motion guidance (clear/cloudy/rain/snow/night)."
    ]
  },
  {
    id: "weather-planning-visual-style-guardrails-added",
    timestamp: "2026-03-02T18:41:16+01:00",
    title: "Weather card planning now enforces existing visual style consistency",
    summary:
      "The weather-adaptive daily-card plan now explicitly requires all weather variants to keep the current card design language and only vary atmospheric mood layers.",
    userHighlights: [
      "Prevents each weather state from feeling like a different component.",
      "Keeps typography, spacing, structure, and interaction consistent across weather themes.",
      "Ensures weather visuals stay in the same recognizable product style."
    ],
    technicalNotes: [
      "Added `Visual Consistency Guardrails` section to `planning_daily_weather_card.md`.",
      "Guardrails define stable card shell/layout and limit weather changes to gradients/overlays/subtle motion."
    ]
  },
  {
    id: "weather-planning-initial-location-vienna",
    timestamp: "2026-03-01T21:34:02+01:00",
    title: "Weather planning updated with Vienna as first location",
    summary:
      "The weather-adaptive daily-card planning now explicitly sets Vienna as the initial/default rollout location for V1.",
    userHighlights: [
      "Initial city for rollout and QA is now clearly defined as Vienna.",
      "Helps align implementation, testing, and demo behavior.",
      "User-specific location support remains planned for later phases."
    ],
    technicalNotes: [
      "Updated `planning_daily_weather_card.md` with an `Initial Location (V1)` section specifying Vienna (`AT`).",
      "No runtime code behavior changed yet."
    ]
  },
  {
    id: "weather-adaptive-daily-card-planning-doc",
    timestamp: "2026-03-01T21:33:04+01:00",
    title: "Added planning document for weather-adaptive daily card",
    summary:
      "A new technical planning document defines architecture and rollout for weather-driven daily card theming with hourly refresh and safe fallbacks.",
    userHighlights: [
      "Plan covers location strategy, weather source, UI theme mapping, refresh cadence, and fallback behavior.",
      "Production-safe V1 focuses on city-based weather via backend proxy (no live GPS required).",
      "Includes clear phased rollout and QA checklist."
    ],
    technicalNotes: [
      "Added `planning_daily_weather_card.md` at repository root.",
      "Document includes proposed backend API contract and frontend update strategy for hourly weather theming."
    ]
  },
  {
    id: "daily-card-read-cta-inline-style-aligned",
    timestamp: "2026-03-01T21:28:47+01:00",
    title: "Daily `Jetzt lesen` CTA aligned to inline cue style",
    summary:
      "The active daily card CTA now uses the same subtle inline style as the done-card open cue, replacing the large filled button look.",
    userHighlights: [
      "Daily and done card actions now feel visually consistent.",
      "No oversized filled button on the active daily card.",
      "Action still stays clear through inline text plus arrow cue."
    ],
    technicalNotes: [
      "Restyled active daily-card action button classes in `reeprise/index.tsx` to inline text treatment.",
      "Added chevron icon to non-loading CTA state while preserving existing click behavior."
    ]
  },
  {
    id: "daily-done-open-cue-non-button-inline",
    timestamp: "2026-03-01T21:26:33+01:00",
    title: "Done-card open indicator changed to non-button inline cue",
    summary:
      "The done-state card no longer uses a button-like open indicator; it now uses a lightweight inline cue while keeping the full card tappable.",
    userHighlights: [
      "Cleaner look without a secondary button feel.",
      "Whole card remains the main interaction target.",
      "Open affordance is still visible via subtle inline label + arrow."
    ],
    technicalNotes: [
      "Replaced bordered/pill `dailyTextDoneOpen` hint with plain inline text cue in `reeprise/index.tsx`.",
      "No behavior changes to click/keyboard navigation."
    ]
  },
  {
    id: "daily-done-text-cleanup-for-natural-preview",
    timestamp: "2026-03-01T21:26:08+01:00",
    title: "Cleaned up done-card title and sentence preview",
    summary:
      "Done-card text extraction was refined to avoid odd-looking raw fragments and produce cleaner, natural-looking title/preview text.",
    userHighlights: [
      "Heading now trims noisy characters and overly long titles.",
      "Sentence preview now removes raw prefixes and clips at word boundaries.",
      "Raw JSON-like payload artifacts are no longer shown as preview text."
    ],
    technicalNotes: [
      "Enhanced daily-card parsing/sanitization helpers in `reeprise/index.tsx` for title and first-sentence extraction.",
      "Added safe clipping rules and JSON-like suppression fallback for done-card teaser content."
    ]
  },
  {
    id: "daily-done-card-tappable-surface-open-cue",
    timestamp: "2026-03-01T21:24:39+01:00",
    title: "Done-state daily card switched from large button to tappable card",
    summary:
      "The large `Öffnen` button was replaced by a cleaner interaction: the whole done-state card is now tappable, with a subtle open indicator.",
    userHighlights: [
      "No oversized button in the done-state card anymore.",
      "A tap/click anywhere on the card opens the text.",
      "A small visual `Open` cue still communicates the action clearly."
    ],
    technicalNotes: [
      "Made done-state daily card container keyboard/tap interactive (`role`, `tabIndex`, Enter/Space handling) in `reeprise/index.tsx`.",
      "Replaced CTA button with a compact open hint chip using `dailyTextDoneOpen` + chevron icon."
    ]
  },
  {
    id: "daily-done-description-from-generated-text-first-sentence",
    timestamp: "2026-03-01T21:23:55+01:00",
    title: "Done-card description now uses first sentence from generated text",
    summary:
      "Instead of static copy, the done-state daily card now displays the first sentence of the generated text (trimmed with `...` when long).",
    userHighlights: [
      "Done-card description now reflects the actual generated text.",
      "Long opening sentences are shortened cleanly with `...`.",
      "Static fallback text still appears when no text sentence is available."
    ],
    technicalNotes: [
      "Added daily-card helpers to parse story body and extract first-sentence preview in `reeprise/index.tsx`.",
      "Done-state description now prefers parsed text preview over `dailyTextDoneDescription` translation."
    ]
  },
  {
    id: "daily-done-title-from-generated-text-and-copy-tightening",
    timestamp: "2026-03-01T21:22:31+01:00",
    title: "Heute-erledigt heading now uses generated text title",
    summary:
      "The done-state daily card now shows the actual generated text title as its heading, and the German done description was shortened.",
    userHighlights: [
      "Replaced generic `Heute erledigt` heading with the real generated title when available.",
      "German done description now reads only `Morgen wartet ein neuer auf dich.`",
      "Fallback remains stable if a parsed title is not available."
    ],
    technicalNotes: [
      "Added local title parsing helper for daily done card and derived heading from today’s resolved daily text (`chatgpt_output` title, then topic fallback).",
      "Updated `dailyTextDoneDescription.GERMAN` translation value in `translations.json`."
    ]
  },
  {
    id: "daily-card-white-line-removed",
    timestamp: "2026-03-01T21:20:40+01:00",
    title: "Removed white line from daily card background",
    summary:
      "The white bottom line/glow accent was removed from the daily card to keep the visual surface cleaner.",
    userHighlights: [
      "No more white line at the bottom of the daily card.",
      "Card looks calmer and more uniform.",
      "Applied consistently to both active and done card states."
    ],
    technicalNotes: [
      "Removed the absolute bottom border/gradient layer from both daily-card render branches in `reeprise/index.tsx`.",
      "No behavior changes."
    ]
  },
  {
    id: "daily-done-open-button-reliability-fallback",
    timestamp: "2026-03-01T21:20:17+01:00",
    title: "Fixed missing open path from Heute-erledigt card",
    summary:
      "The done-state daily card now reliably keeps an open button by using broader same-day matching and marker backfill for older stored states.",
    userHighlights: [
      "You can open today’s generated text from the Heute-erledigt card more reliably.",
      "Older done markers without a stored text id are now auto-repaired when today’s text is found.",
      "Done-state remains visually unchanged while interaction is more robust."
    ],
    technicalNotes: [
      "Added fallback same-day practice-text detection (without strict topic-date dependency) in `DailyPracticeCallout`.",
      "When a target text id is resolved, the local daily marker is backfilled with `{ date, textId }` to stabilize future opens."
    ]
  },
  {
    id: "daily-done-card-openability-and-style-alignment",
    timestamp: "2026-03-01T21:18:38+01:00",
    title: "Heute-erledigt card now keeps open access and matches daily card style",
    summary:
      "The completed daily card now follows the same sky/cloud design as the active daily card and still lets users open the generated text reliably.",
    userHighlights: [
      "Done-state card now visually matches the regular daily card.",
      "You can open today’s generated text directly from the done-state card.",
      "Open access remains stable even during list refresh timing delays."
    ],
    technicalNotes: [
      "Extended daily done marker storage to support `{ date, textId }` (while remaining compatible with previous string values).",
      "Done-state card now resolves open target from either list match or stored marker `textId`, and reuses the same sky/cloud layout as the active card in `reeprise/index.tsx`."
    ]
  },
  {
    id: "daily-cloud-animation-visibility-boost",
    timestamp: "2026-03-01T21:15:29+01:00",
    title: "Increased visibility of daily card cloud animation",
    summary:
      "Cloud movement on the daily card was intensified so the animation is clearly visible instead of nearly static.",
    userHighlights: [
      "Cloud drift is now easier to perceive at a glance.",
      "Motion remains smooth and calm, but no longer feels frozen.",
      "Reduced-motion users still get a static version."
    ],
    technicalNotes: [
      "Adjusted `daily-cloud-drift-left/right` keyframes in `src/index.css` to use larger translate offsets and subtle opacity/scale variation.",
      "Shortened animation durations per cloud layer to improve perceivable movement."
    ]
  },
  {
    id: "daily-text-card-cloud-animation",
    timestamp: "2026-03-01T21:13:35+01:00",
    title: "Added subtle cloud animation to the daily text card",
    summary:
      "The daily text card now has gentle cloud movement to make the morning surface feel more alive without being distracting.",
    userHighlights: [
      "Clouds now drift slowly across the daily card background.",
      "Animation is subtle and readability-focused.",
      "Motion is automatically disabled for users with reduced-motion preference."
    ],
    technicalNotes: [
      "Added `daily-cloud-*` classes to daily-card cloud layers in `reeprise/index.tsx`.",
      "Implemented `daily-cloud-drift-left/right` keyframes and reduced-motion media handling in `src/index.css`."
    ]
  },
  {
    id: "daily-and-uebungstext-button-style-aligned",
    timestamp: "2026-03-01T21:12:06+01:00",
    title: "Aligned Übungstext and daily card button style",
    summary:
      "The main CTA buttons on the Übungstext and daily cards now use the same shape and blue style for a consistent visual language.",
    userHighlights: [
      "Both cards now use the same rounded-full button shape.",
      "Color, border, hover, and disabled states are now consistent.",
      "Button typography now follows the same uppercase CTA style."
    ],
    technicalNotes: [
      "Updated the Übungstext banner CTA classes in `GenerateTextCard.tsx` to match the daily card CTA style from `reeprise/index.tsx`.",
      "No behavior logic changes."
    ]
  },
  {
    id: "uebungstext-placement-switched-back-to-generator-card",
    timestamp: "2026-03-01T21:11:21+01:00",
    title: "Übungstext card moved back to the generator panel",
    summary:
      "The Übungstext card placement was switched back: the generator card version is visible again and the separate top-area callout was removed.",
    userHighlights: [
      "Übungstext is now back in its original generator area.",
      "The extra card near the daily section is removed.",
      "Only one Übungstext card remains visible."
    ],
    technicalNotes: [
      "Removed `StruggleWordsCallout` rendering and implementation from `reeprise/index.tsx`.",
      "Restored default `GenerateTextCard` usage without `showStruggleBanner={false}` override."
    ]
  },
  {
    id: "uebungstext-duplicate-card-removed",
    timestamp: "2026-03-01T21:10:13+01:00",
    title: "Removed duplicate Übungstext card on Reeprise page",
    summary:
      "The duplicate Übungstext card was removed by keeping only the top callout near the daily card.",
    userHighlights: [
      "Only one Übungstext card is now visible on the page.",
      "Top placement next to the daily flow is preserved.",
      "Generator form stays cleaner without repeated callout content."
    ],
    technicalNotes: [
      "Added `showStruggleBanner` prop to `GenerateTextCard` (default `true`).",
      "Set `showStruggleBanner={false}` in `reeprise/index.tsx` to avoid rendering the in-card duplicate."
    ]
  },
  {
    id: "uebungstext-card-no-cloud-shapes",
    timestamp: "2026-03-01T21:09:25+01:00",
    title: "Removed cloud shapes from Übungstext card design",
    summary:
      "The Übungstext cards now keep the blue style but no longer include cloud-form decorations.",
    userHighlights: [
      "Cleaner Übungstext card look with less visual ornament.",
      "Blue color direction stays consistent with the current theme.",
      "Both Übungstext surfaces now follow the same no-cloud style."
    ],
    technicalNotes: [
      "Removed cloud shape overlay markup from the Reeprise `StruggleWordsCallout`.",
      "Removed cloud shape overlay markup from the `GenerateTextCard` struggle banner block."
    ]
  },
  {
    id: "uebungstext-callout-added-near-daily-card",
    timestamp: "2026-03-01T21:08:33+01:00",
    title: "Übungstext card also added to the daily-card area",
    summary:
      "The Übungstext callout now appears in the same top Reeprise section as the daily card, so users can access it immediately without opening the generator panel.",
    userHighlights: [
      "Übungstext is now visible next to the morning daily flow.",
      "One tap still creates and opens the generated practice text.",
      "Visual style remains aligned with the blue sky/cloud design."
    ],
    technicalNotes: [
      "Added `StruggleWordsCallout` to `reeprise/index.tsx` and rendered it below `DailyPracticeCallout`.",
      "Hooked it to `useStruggleWords` + `useGeneratedTextCreate` and reused the per-language 24h hide gate from localStorage."
    ]
  },
  {
    id: "uebungstext-card-sky-cloud-style",
    timestamp: "2026-03-01T21:07:23+01:00",
    title: "Übungstext card redesigned to match the sky/cloud visual style",
    summary:
      "The practice-text card in the generator now follows the same calm blue-sky direction as the daily card, with a compact cloud-themed surface.",
    userHighlights: [
      "Übungstext card now feels visually consistent with the morning daily card.",
      "Color tone is calmer and less noisy.",
      "Action button style now matches the blue visual direction."
    ],
    technicalNotes: [
      "Restyled the struggle-word callout block in `GenerateTextCard.tsx` with sky gradient and layered cloud shapes.",
      "Kept existing behavior and logic unchanged; this was a styling-only update without drop shadows."
    ]
  },
  {
    id: "daily-text-title-duplicate-removed",
    timestamp: "2026-03-01T21:06:33+01:00",
    title: "Removed duplicate `Text des Tages` labeling",
    summary:
      "Only the morning daily card now uses the `Text des Tages` title; the separate struggle-word card has distinct wording again.",
    userHighlights: [
      "No more second card with the same `Text des Tages` headline.",
      "Exercise card is now clearly labeled as `Übungstext`.",
      "Card descriptions are now distinct and less confusing."
    ],
    technicalNotes: [
      "Updated translation keys `struggle_words_title` and `struggle_words_description` in `translations.json`.",
      "No logic or routing changes."
    ]
  },
  {
    id: "daily-text-card-blue-sky-cloud-redesign",
    timestamp: "2026-03-01T21:05:41+01:00",
    title: "Daily morning card redesigned to realistic blue-sky cloud direction",
    summary:
      "The first daily card now uses a calmer sky-blue visual system with layered cloud forms instead of a multi-color palette.",
    userHighlights: [
      "Card now feels cleaner and more motivating in the morning.",
      "Background follows a realistic blue-sky gradient with soft cloud structure.",
      "Primary action now matches the blue direction for a consistent look."
    ],
    technicalNotes: [
      "Replaced warm gradient and accent classes in `DailyPracticeCallout` with sky-blue palette classes.",
      "Added absolute layered cloud shape elements (radial gradients + rounded forms) without drop shadows."
    ]
  },
  {
    id: "daily-text-copy-positive-german-tone",
    timestamp: "2026-03-01T21:04:28+01:00",
    title: "Daily text German description changed to positive wording",
    summary:
      "The daily card description in German now avoids negative framing and uses a more encouraging tone.",
    userHighlights: [
      "Removed wording that could feel corrective or discouraging.",
      "Daily card now reads as a positive next-step invitation.",
      "Tone remains clear that this is your daily text."
    ],
    technicalNotes: [
      "Updated German values for `dailyTextDescription` and aligned `struggle_words_description` in `translations.json`.",
      "No behavior or layout logic changed."
    ]
  },
  {
    id: "daily-text-card-warm-morning-palette",
    timestamp: "2026-03-01T21:03:53+01:00",
    title: "Daily text morning card shifted to a warmer, less dark style",
    summary:
      "The first card users see in the morning now avoids the previous black-heavy look and uses a softer, more inviting color direction.",
    userHighlights: [
      "The daily card now uses a warm sunrise-like background instead of cool gray.",
      "Date/weekday accents now feel lighter and more editorial.",
      "The main action button is no longer black and feels more inviting to start reading."
    ],
    technicalNotes: [
      "Updated `DailyPracticeCallout` container, text, and badge color classes in `reeprise/index.tsx`.",
      "Changed CTA from slate/black styling to a warm rose accent while keeping behavior unchanged."
    ]
  },
  {
    id: "daily-text-copy-text-des-tages",
    timestamp: "2026-03-01T21:03:13+01:00",
    title: "Daily card copy updated to `Text des Tages`",
    summary:
      "The morning daily-text card now uses clearer German wording so it is immediately understood as the daily text.",
    userHighlights: [
      "Headline changed from `Dein Text für heute` to `Text des Tages`.",
      "Description now explicitly says this is your daily text.",
      "Wording stays warm but now communicates the daily rhythm more clearly."
    ],
    technicalNotes: [
      "Updated `dailyTextHeading` and `dailyTextDescription` German translations in `translations.json`.",
      "Aligned legacy `struggle_words_title` and `struggle_words_description` German strings to the same wording."
    ]
  },
  {
    id: "daily-text-done-state-latched-per-day",
    timestamp: "2026-03-01T21:02:27+01:00",
    title: "Daily text done-state now stays latched for the day",
    summary:
      "The compact `done for today` card no longer flips back to the full create card during short data refresh transitions.",
    userHighlights: [
      "Once today is marked done, the compact done card stays stable.",
      "Temporary list/query refresh timing no longer causes card state bouncing.",
      "The card still resets normally for the next day."
    ],
    technicalNotes: [
      "Added a day+language-scoped `doneLatched` UI state in `DailyPracticeCallout`.",
      "Latched state is reset only when the day or language bucket changes, and is promoted to `true` whenever done conditions become true."
    ]
  },
  {
    id: "daily-text-done-state-flicker-fix",
    timestamp: "2026-03-01T20:59:59+01:00",
    title: "Fixed daily text done-state flicker after creation",
    summary:
      "The collapsed `done for today` daily-text card now remains stable instead of briefly switching back to the full creation card.",
    userHighlights: [
      "After creating today’s text, the compact done-state stays visible consistently.",
      "State now survives route changes and short list refresh delays.",
      "A fresh full card still appears on the next day."
    ],
    technicalNotes: [
      "Moved daily-done marker from volatile component state to localStorage-backed state.",
      "Marker matching now covers both current language-space and resolved generation language to avoid transient mismatches."
    ]
  },
  {
    id: "daily-text-card-collapses-to-done-state",
    timestamp: "2026-03-01T20:48:06+01:00",
    title: "Daily text card now collapses into a done-for-today state",
    summary:
      "After creating today’s daily text, the large card now collapses into a compact completion state instead of disappearing entirely.",
    userHighlights: [
      "You now get a clear `done for today` confirmation once your daily text is created.",
      "A compact card remains visible and can open today’s daily text directly.",
      "On the next day, the full creation card appears again automatically."
    ],
    technicalNotes: [
      "Replaced the previous hide-on-create behavior in `DailyPracticeCallout` with a conditional compact completion surface.",
      "Added new translation keys for done-state heading, description, and open action in German and English."
    ]
  },
  {
    id: "daily-text-heading-explicitly-today",
    timestamp: "2026-03-01T20:45:22+01:00",
    title: "Daily text heading now explicitly says it is for today",
    summary:
      "The callout headline was refined so users can immediately recognize the card as today’s daily text.",
    userHighlights: [
      "German headline now reads `Dein Text für heute`.",
      "English headline now reads `Your story for today`.",
      "Warm tone remains while daily intent is now explicit."
    ],
    technicalNotes: [
      "Updated only `dailyTextHeading` translation values in `translations.json`.",
      "No component logic or layout changes."
    ]
  },
  {
    id: "daily-text-callout-warm-non-school-copy",
    timestamp: "2026-03-01T20:44:48+01:00",
    title: "Daily text card copy rewritten to feel warmer and less school-like",
    summary:
      "The daily text callout wording was rewritten to sound more personal and inviting, with less technical and classroom-like phrasing.",
    userHighlights: [
      "Headline now reads as a personal invitation instead of a utility label.",
      "Description now feels softer and more human while keeping the same meaning.",
      "CTA now uses a more natural reading-focused wording."
    ],
    technicalNotes: [
      "Updated existing `dailyTextHeading`, `dailyTextDescription`, and `dailyTextCta` entries in translations without changing keys or component logic.",
      "Applied in both German and English translation values."
    ]
  },
  {
    id: "daily-text-callout-compact-tone-down",
    timestamp: "2026-03-01T20:43:59+01:00",
    title: "Daily text callout scaled down to a calmer compact style",
    summary:
      "The newly redesigned morning callout was intentionally reduced in visual intensity and size for a cleaner first-view balance.",
    userHighlights: [
      "Card now uses a more compact footprint with smaller headline and tighter spacing.",
      "Date/weekday row remains, but with lighter visual emphasis.",
      "CTA remains clear while feeling less dominant."
    ],
    technicalNotes: [
      "Reduced typography scale, spacing, and border weight in `DailyPracticeCallout`.",
      "Removed oversized decorative ticket-cut accents and simplified surface treatment."
    ]
  },
  {
    id: "daily-text-callout-morning-first-impression-redesign",
    timestamp: "2026-03-01T20:42:28+01:00",
    title: "Daily text morning callout redesigned as a standout first-impression card",
    summary:
      "The morning `Ein Text für dich …` callout was redesigned to feel more collectible and editorial, with a stronger reading invitation and built-in weekday/date context.",
    userHighlights: [
      "The card now presents weekday and date at the top for stronger daily rhythm.",
      "Headline and CTA hierarchy were rebuilt so the card feels like a featured daily drop.",
      "Visual treatment now uses a unique border + ticket-cut motif without drop shadows."
    ],
    technicalNotes: [
      "Reworked `DailyPracticeCallout` layout in `reeprise/index.tsx` with new grid structure, typographic hierarchy, and responsive CTA behavior.",
      "Added locale-aware weekday/date formatting derived from UI language (`de-DE`/`en-US`) and applied a shadow-free gradient + border visual system."
    ]
  },
  {
    id: "daily-text-callout-editorial-openai-color-restyle",
    timestamp: "2026-03-01T20:39:06+01:00",
    title: "Daily text callout redesigned with editorial layout and fresh color style",
    summary:
      "The `Ein Text für dich …` daily text callout now uses an editorial text-details-inspired structure with a modern color-forward treatment.",
    userHighlights: [
      "Heading and description now follow a cleaner magazine-style hierarchy.",
      "The card now uses soft atmospheric color gradients and subtle depth.",
      "The CTA button is now a stronger rounded accent action while keeping the same behavior."
    ],
    technicalNotes: [
      "Restyled `DailyPracticeCallout` container with layered radial gradients, soft shadow, and decorative blur shapes.",
      "Updated callout typography, divider rhythm, and CTA styling in `reeprise/index.tsx` without changing translations or business logic."
    ]
  },
  {
    id: "speech-timestamps-migration-compat-safety",
    timestamp: "2026-03-01T20:36:29+01:00",
    title: "Added migration safety guard for speech timestamp columns",
    summary:
      "Database startup is now more resilient when local migration history and schema drift differ for speech timestamp fields.",
    userHighlights: [
      "Reduced risk of local crashes caused by mixed migration states.",
      "Story generation setup is more stable after branch switching.",
      "No visible UI behavior changes."
    ],
    technicalNotes: [
      "Added an idempotent compatibility migration that ensures `speech_timestamps` exists on `reeprise_generatedtext` and `reeprise_chapter` in SQLite environments.",
      "Migration checks actual table columns first and only applies `ALTER TABLE` when a column is missing."
    ]
  },
  {
    id: "text-details-audio-readalong-highlight-rollback",
    timestamp: "2026-03-01T20:31:36+01:00",
    title: "Audio read-along highlighting was rolled back",
    summary:
      "The recently added audio-synced text highlighting on text details was removed again.",
    userHighlights: [
      "Text no longer auto-highlights during audio playback.",
      "Audio playback and chapter-specific audio selection continue to work as before.",
      "Rollback was applied to avoid shipping low-quality highlighting behavior."
    ],
    technicalNotes: [
      "Removed frontend audio-time-driven text highlight wiring and related highlight computation.",
      "Removed backend timestamp-generation/storage plumbing introduced for audio word highlighting."
    ]
  },
  {
    id: "chapter-specific-audio-playback-in-text-details",
    timestamp: "2026-03-01T20:18:24+01:00",
    title: "Audio playback now follows the active chapter",
    summary:
      "When you generate and open a new chapter from selected vocabulary, the audio player now loads that chapter’s own speech instead of replaying the first/base text audio.",
    userHighlights: [
      "Chapter audio now matches the chapter you are currently viewing.",
      "Switching chapter or opening another text no longer reuses stale audio playback state.",
      "Pressing play requests speech generation for the current chapter context."
    ],
    technicalNotes: [
      "Added chapter-aware speech support in backend: `Chapter` now stores `speech`, text-to-speech accepts `chapter_number`, and streaming endpoint reads `?chapter=<counter>`.",
      "Updated text-details player to build chapter-specific audio URLs, request speech with chapter context, and reset player state when text/chapter changes."
    ]
  },
  {
    id: "text-details-chapter-buttons-moved-into-text-column-and-scaled-down",
    timestamp: "2026-03-01T20:09:25+01:00",
    title: "Chapter buttons moved into text column and resized smaller",
    summary:
      "Previous/next chapter controls now sit directly in the main text column and use a smaller visual footprint for a cleaner article layout.",
    userHighlights: [
      "Vorheriges/Nächstes Kapitel now appear at the top of the main text column.",
      "Buttons are smaller and less dominant while still easy to tap/click.",
      "Right sidebar no longer carries its own separate chapter button."
    ],
    technicalNotes: [
      "Removed standalone mobile top-row and sidebar chapter-button placements; added shared button row above the text/audio area.",
      "Reduced chapter button typography, spacing, and icon gaps in `LastChapterNavigationTile` and `NextChapterNavigationTile`."
    ]
  },
  {
    id: "text-details-chapter-buttons-top-padding",
    timestamp: "2026-03-01T20:08:32+01:00",
    title: "Added top padding to previous/next chapter buttons",
    summary:
      "Both chapter navigation controls now include additional top padding for more balanced vertical spacing.",
    userHighlights: [
      "Vorheriges Kapitel button now has extra top breathing room.",
      "Nächstes Kapitel button now matches the same top spacing.",
      "Applied consistently to active and disabled states."
    ],
    technicalNotes: [
      "Updated shared chapter button class sets in `LastChapterNavigationTile` and `NextChapterNavigationTile` to include top padding.",
      "No behavior, routing, or translation changes."
    ]
  },
  {
    id: "text-details-next-chapter-to-vertiefen-spacing",
    timestamp: "2026-03-01T20:08:00+01:00",
    title: "Added spacing between Next Chapter and Vertiefen on desktop",
    summary:
      "The desktop layout now includes clear vertical spacing between the `Nächstes Kapitel` control and the `Vertiefen` section.",
    userHighlights: [
      "The two sections no longer touch visually.",
      "Desktop right-column rhythm is cleaner and easier to scan.",
      "No behavior changes to chapter navigation or helper panel."
    ],
    technicalNotes: [
      "Added desktop bottom margin on the `NextChapterNavigationTile` wrapper before `LanguageHelperPanel` in generated text details route.",
      "No API, logic, or translation changes."
    ]
  },
  {
    id: "text-details-vertiefen-collapse-empty-chat-gap",
    timestamp: "2026-03-01T19:54:31+01:00",
    title: "Vertiefen no longer leaves a large empty gap before first message",
    summary:
      "When the helper chat is empty, the intermediate conversation area now collapses so the input sits directly under the Vertiefen title.",
    userHighlights: [
      "Reduced vertical whitespace between `Vertiefen` and the input field when no messages exist.",
      "Conversation area appears only once there are messages or loading state.",
      "No change to ask/send behavior."
    ],
    technicalNotes: [
      "Added conditional rendering for the message rail in `LanguageHelperPanel` based on message/loading presence.",
      "Retained existing message, action, and loading UI once conversation content exists."
    ]
  },
  {
    id: "text-details-vertiefen-send-button-alignment-fix",
    timestamp: "2026-03-01T19:53:32+01:00",
    title: "Vertiefen send button alignment refined",
    summary:
      "The `Senden` control in Vertiefen was adjusted for cleaner pill geometry and centered label alignment.",
    userHighlights: [
      "Send button shape now appears visually balanced within the input row.",
      "Label is centered more consistently inside the rounded control.",
      "No behavior changes to sending messages."
    ],
    technicalNotes: [
      "Adjusted helper send-button dimensions from fixed width to min-width plus horizontal padding and explicit line-height normalization.",
      "Kept existing interaction and disabled-state behavior."
    ]
  },
  {
    id: "text-details-vertiefen-remove-all-divider-lines",
    timestamp: "2026-03-01T19:52:51+01:00",
    title: "Vertiefen section divider lines removed",
    summary:
      "All horizontal divider lines were removed from the Vertiefen helper section for a cleaner, lighter layout.",
    userHighlights: [
      "No horizontal separator lines remain in the Vertiefen panel.",
      "The section now appears visually cleaner and less cluttered.",
      "Helper behavior and messaging remain unchanged."
    ],
    technicalNotes: [
      "Removed remaining border styling from the helper message rail wrapper in `LanguageHelperPanel`.",
      "No logic, API, or translation changes."
    ]
  },
  {
    id: "text-details-vertiefen-single-divider-line",
    timestamp: "2026-03-01T19:52:25+01:00",
    title: "Vertiefen section now uses a single divider line",
    summary:
      "The helper section divider was simplified from two horizontal lines to one for a cleaner editorial layout.",
    userHighlights: [
      "Removed one of the two horizontal lines in `Vertiefen`.",
      "Section now feels less busy while preserving structure.",
      "All helper interactions remain unchanged."
    ],
    technicalNotes: [
      "Changed message rail container from `border-y` to `border-t` in `LanguageHelperPanel`.",
      "No behavioral or API changes."
    ]
  },
  {
    id: "text-details-vertiefen-editorial-restyle",
    timestamp: "2026-03-01T19:50:09+01:00",
    title: "Vertiefen section now follows the editorial text-details style",
    summary:
      "The `Vertiefen` helper panel on text details now uses the same newspaper-like visual language as the surrounding layout.",
    userHighlights: [
      "Panel heading now uses compact editorial metadata styling.",
      "Message area now sits inside subtle divider rails for cleaner article rhythm.",
      "Input and send controls are now visually aligned with the outlined pill/button style used across text details."
    ],
    technicalNotes: [
      "Restyled `LanguageHelperPanel` container, message bubbles, loading state, confirmation card typography, and input action controls.",
      "No helper behavior, API contract, or translation key changes."
    ]
  },
  {
    id: "text-details-vocabulary-card-editorial-restyle",
    timestamp: "2026-03-01T19:49:04+01:00",
    title: "Text-details vocabulary cards now match editorial page styling",
    summary:
      "Vocabulary cards on text details now use the same newspaper-like visual language as the rest of the page, with quieter surfaces, serif hierarchy, and subtle controls.",
    userHighlights: [
      "Vocabulary cards now appear as clean white editorial panels instead of heavy gray blocks.",
      "Sentence and grammar areas use thin divider sections for better article-like rhythm.",
      "More grammar and edit controls now match the compact outlined style used across text details."
    ],
    technicalNotes: [
      "Restyled `VocabularyItem` shell, sentence/grammar sections, grammar chips, and control buttons in the generated-text details route.",
      "No behavior, data, or translation changes."
    ]
  },
  {
    id: "text-details-generate-new-text-cta-editorial-restyle",
    timestamp: "2026-03-01T19:47:05+01:00",
    title: "Generate-new-text action now matches editorial text-details style",
    summary:
      "The `Neuen Text aus Vokabeln generieren` action on text details now uses the same compact, newspaper-like visual language as the rest of the page.",
    userHighlights: [
      "Desktop action is now presented in a subtle bordered section with a compact pill button.",
      "Mobile sticky action now aligns with content gutters and uses the same editorial button styling.",
      "Action behavior is unchanged; only visual design was updated."
    ],
    technicalNotes: [
      "Restyled both desktop and mobile `btn_Generate_new_Text` surfaces in generated text details route to a border-based, uppercase tracked editorial CTA pattern.",
      "No translation, backend, or routing logic changes."
    ]
  },
  {
    id: "text-details-mobile-chapter-nav-side-padding",
    timestamp: "2026-03-01T19:46:14+01:00",
    title: "Mobile chapter navigation now aligns with page side gutters",
    summary:
      "The mobile row for `Vorheriges Kapitel` and `Nächstes Kapitel` now includes side padding so controls no longer sit flush against the viewport edges.",
    userHighlights: [
      "Chapter navigation buttons now align with the same horizontal gutters as text content.",
      "Touch targets keep the same behavior but feel better placed on small screens.",
      "Visual rhythm between nav controls and article body is now consistent."
    ],
    technicalNotes: [
      "Added horizontal padding to the mobile-only chapter navigation wrapper in generated text details route.",
      "No behavior, routing, or state changes."
    ]
  },
  {
    id: "text-details-chapter-nav-editorial-restyle",
    timestamp: "2026-03-01T19:45:03+01:00",
    title: "Chapter navigation now matches text-details editorial UI",
    summary:
      "The `Vorheriges Kapitel` and `Nächstes Kapitel` controls on text details now use the same compact, border-based visual language as the rest of the page.",
    userHighlights: [
      "Previous/next chapter controls now appear as subtle rounded pills instead of plain text rows.",
      "Active and disabled states are clearer and more consistent with surrounding metadata controls.",
      "The chapter navigation feels integrated with the article-like layout on mobile and desktop."
    ],
    technicalNotes: [
      "Restyled `LastChapterNavigationTile` and `NextChapterNavigationTile` to use consistent compact border-pill classes and uppercase tracking.",
      "No behavior change to routing/search params; update is visual only."
    ]
  },
  {
    id: "text-details-remove-published-label",
    timestamp: "2026-03-01T19:43:46+01:00",
    title: "Text details date row now hides the Published label",
    summary:
      "The text-details metadata row now shows only the date and time, without the extra `Published/Veröffentlicht` label.",
    userHighlights: [
      "Removed the `Veröffentlicht`/`Published` caption above text date metadata.",
      "Date and time remain visible in the same position under the title.",
      "The header area now looks cleaner and less repetitive."
    ],
    technicalNotes: [
      "Removed rendering of `text_details_published_label` in `GeneratedTextDetailsTextPane` while keeping existing locale-aware date/time formatting.",
      "No backend or API changes."
    ]
  },
  {
    id: "generated-text-strip-title-story-for-visual-and-audio",
    timestamp: "2026-03-01T19:43:06+01:00",
    title: "Generated texts now remove Title/Story wrappers before display and speech",
    summary:
      "Generated text content now strips leading `Title`/`Story` wrapper labels at save-time so the reading view and audio playback contain only the actual story body.",
    userHighlights: [
      "Text details no longer shows raw `Title:` or `Story:` wrapper labels.",
      "Audio playback no longer reads `Title` or `Story` out loud before the real text.",
      "Applies to plain labeled outputs in addition to JSON-style model responses."
    ],
    technicalNotes: [
      "Backend model-output extraction now handles plain `Title:` + `Story:` formats and returns clean story body plus parsed title.",
      "Completion flow now persists cleaned `chatgpt_output` after wrapper removal and includes regression test coverage for plain labeled payloads."
    ]
  },
  {
    id: "text-details-audio-timeline-knob",
    timestamp: "2026-03-01T19:41:28+01:00",
    title: "Text details audio timeline now has a draggable knob",
    summary:
      "The audio progress line on text details now includes a visible circular handle so users can scrub more precisely without changing the existing player layout.",
    userHighlights: [
      "A small circle now shows the current playback position on the timeline.",
      "You can drag the circle to replay or skip to exact moments more easily.",
      "The rest of the audio player design stays the same."
    ],
    technicalNotes: [
      "Converted timeline wrapper to a relative track container and added a positioned handle tied to playback progress percentage.",
      "Wired handle mouse/touch start events into existing seek/scrub logic while preserving click-to-seek behavior on the line."
    ]
  },
  {
    id: "text-details-audio-progress-line-scrub",
    timestamp: "2026-03-01T19:39:39+01:00",
    title: "Text details audio progress line is now interactive",
    summary:
      "The audio progress line on text details now supports click and drag scrubbing so listeners can quickly replay or skip sections without changing the player layout.",
    userHighlights: [
      "Click anywhere on the progress line to jump to that moment in the audio.",
      "Drag across the line to scrub backward or forward like modern streaming players.",
      "The existing compact editorial audio design stays unchanged."
    ],
    technicalNotes: [
      "Added timeline seek handling based on pointer position against the rendered progress line bounds.",
      "Implemented mouse and touch scrubbing listeners that update `audio.currentTime` and keep UI progress state in sync."
    ]
  },
  {
    id: "text-details-audio-editorial-player-layout",
    timestamp: "2026-03-01T18:11:52+01:00",
    title: "Text details audio player now uses a compact editorial layout",
    summary:
      "The text-details audio section has been redesigned from a large card into a slimmer, article-style metadata row for a cleaner reading experience.",
    userHighlights: [
      "Play controls now appear in a compact row that fits better with article typography.",
      "Playback now shows a subtle time readout and slim progress line while listening.",
      "Audio speed can be toggled inline without opening additional UI."
    ],
    technicalNotes: [
      "Replaced oversized audio container/button styles with border-y editorial meta row styling in generated text details route.",
      "Added lightweight playback metadata state (`currentTime`, `duration`, `playbackRate`) and bound it to audio element events for time/progress/speed display."
    ]
  },
  {
    id: "generated-title-headline-normalization-and-topic-echo-guard",
    timestamp: "2026-03-01T18:01:17+01:00",
    title: "Generated text titles now start uppercase and avoid topic-only headers",
    summary:
      "Generated text headers are now enforced as headline-style titles: they always start uppercase and are no longer allowed to just repeat the typed topic.",
    userHighlights: [
      "Generated titles now consistently begin with an uppercase letter.",
      "If the model returns a title that only mirrors your topic, the system rewrites it into a content-based title.",
      "Text headers better reflect the actual generated story content."
    ],
    technicalNotes: [
      "Backend completion flow now checks title/topic similarity and treats exact topic echoes as invalid final titles.",
      "When needed, backend requests a story-based AI title and falls back to derived content titles, then applies headline normalization before persisting `topic`."
    ]
  },
  {
    id: "ui-language-date-time-and-brand-copy-consistency",
    timestamp: "2026-03-01T17:29:29+01:00",
    title: "UI language now controls generated-text date/time formatting",
    summary:
      "Generated-text date/time displays now follow the app UI language, and the language-switch subtitle copy is fully translated.",
    userHighlights: [
      "When app UI is German, generated-text date/time metadata now formats in German style.",
      "Text-details publication metadata also follows app UI language selection.",
      "The `with Uxhulu` subtitle in language switcher is now translatable."
    ],
    technicalNotes: [
      "Extended `useTranslation` return payload with current UI language and used it to map locale (`de-DE`/`en-US`) in generated-text surfaces.",
      "Replaced hardcoded `with Uxhulu` in app route with translation key `language_switch_with_brand`."
    ]
  },
  {
    id: "generated-title-vs-prompt-separation",
    timestamp: "2026-03-01T17:23:27+01:00",
    title: "Text headers now use generated titles while prompt preview stays user-typed",
    summary:
      "Text card headers and prompt previews are now separated: headers use generated titles, while preview lines keep the original typed prompt.",
    userHighlights: [
      "If you type `rose` as topic, the preview can keep `rose` while header shows a generated title.",
      "Header no longer needs to reuse prompt text when no explicit title was returned.",
      "The same prompt preview behavior is available on text details."
    ],
    technicalNotes: [
      "Backend now always derives a title from story content when no explicit title exists and persists it as `topic`.",
      "Frontend caches original create-form topic per generated text id and uses that cache for prompt preview lines."
    ]
  },
  {
    id: "text-details-article-meta-date-time",
    timestamp: "2026-03-01T17:20:55+01:00",
    title: "Text details now shows article-style publication date and time",
    summary:
      "The text details header now includes an article-style publication metadata row with generation date and time.",
    userHighlights: [
      "Date and time now appear directly below the text title.",
      "Presentation uses a subtle editorial style to support an article-like reading vibe.",
      "Works for existing and new generated texts using stored `created_at`."
    ],
    technicalNotes: [
      "Added formatted publication metadata block in `GeneratedTextDetailsTextPane` with locale-aware date/time formatting.",
      "Added translation key `text_details_published_label`."
    ]
  },
  {
    id: "prompt-action-label-discreet",
    timestamp: "2026-03-01T17:19:37+01:00",
    title: "Prompt action label is now more discreet",
    summary:
      "The prompt expansion action now uses a shorter, less intrusive label.",
    userHighlights: [
      "Changed button wording from `Vollen Prompt` to `Mehr` (EN: `More`).",
      "This keeps the UI calmer while retaining access to full prompt content.",
      "No behavior changes to the full prompt modal."
    ],
    technicalNotes: [
      "Updated translation key `text_card_open_full_prompt` only.",
      "No layout or backend changes."
    ]
  },
  {
    id: "text-details-topic-preview-and-full-prompt",
    timestamp: "2026-03-01T17:18:51+01:00",
    title: "Text details now shows topic preview with full prompt option",
    summary:
      "The text details header now includes a compact topic preview line and lets you open the complete prompt when truncated.",
    userHighlights: [
      "Topic preview is now visible in text details below the header.",
      "Long topics are shortened to four words with `...` for readability.",
      "You can open a modal to read the full prompt text."
    ],
    technicalNotes: [
      "Added topic-preview row and full-prompt modal behavior to `GeneratedTextDetailsTextPane`.",
      "Reused existing prompt action translation keys from text-card prompt UI."
    ]
  },
  {
    id: "text-card-header-no-story-sentence-fallback",
    timestamp: "2026-03-01T17:17:28+01:00",
    title: "Text card header no longer falls back to first story sentence",
    summary:
      "The text card header now uses only actual title sources and no longer derives a heading from the story body.",
    userHighlights: [
      "First line/sentence of story text no longer appears in the card header.",
      "Header remains reserved for title content.",
      "Topic preview behavior remains unchanged."
    ],
    technicalNotes: [
      "Removed story-based title derivation fallback in `ReepriseTextCard`.",
      "Header resolution now uses parsed title only, then existing fallback behavior."
    ]
  },
  {
    id: "text-card-open-full-prompt-modal",
    timestamp: "2026-03-01T17:14:27+01:00",
    title: "Text cards now include an Open full prompt action",
    summary:
      "Long topic prompts can now be opened in full from the card via a dedicated action.",
    userHighlights: [
      "When a prompt is truncated in the topic line, you now get an `Open full prompt` button.",
      "Clicking it opens a modal with the complete prompt text.",
      "Works on both standard and podcast text cards."
    ],
    technicalNotes: [
      "Added local prompt modal state to `ReepriseTextCard` and wired open actions where topic preview truncation is active.",
      "Introduced new translation keys for open/full/close prompt labels."
    ]
  },
  {
    id: "text-card-topic-line-persistent",
    timestamp: "2026-03-01T17:13:01+01:00",
    title: "Text card topic line now stays visible after title resolves",
    summary:
      "The small topic/prompt line is now persistent and no longer disappears once the generated title appears.",
    userHighlights: [
      "Topic stays visible as secondary context at all times.",
      "Generated title still remains the primary header.",
      "Long topics continue to use 4-word ellipsis in the secondary line."
    ],
    technicalNotes: [
      "Removed conditional hiding logic for topic line in `ReepriseTextCard`; topic now renders whenever present.",
      "No backend changes."
    ]
  },
  {
    id: "text-card-topic-preview-word-truncation",
    timestamp: "2026-03-01T17:12:06+01:00",
    title: "Text card topic preview now truncates long prompts",
    summary:
      "Long topic prompts shown in the small secondary line are now shortened after four words and displayed with ellipsis.",
    userHighlights: [
      "Secondary topic text remains compact and readable for very long prompts.",
      "Prompt preview now shows first four words followed by `...` when needed.",
      "Full prompt is still available on hover via tooltip."
    ],
    technicalNotes: [
      "Added shared topic-preview truncation helper in `ReepriseTextCard` and applied it to all secondary topic placements.",
      "No backend changes."
    ]
  },
  {
    id: "text-card-stable-topic-placement-during-generation",
    timestamp: "2026-03-01T17:11:13+01:00",
    title: "Text cards keep prompt/topic in secondary line during generation",
    summary:
      "While a new text is still generating, the user prompt/topic now stays in the small secondary topic slot instead of temporarily appearing as the main header.",
    userHighlights: [
      "No visual jump from header to secondary line when the generated title appears.",
      "Header now shows a neutral `Generating title…` placeholder until a real title is available.",
      "Topic placement stays stable from first render."
    ],
    technicalNotes: [
      "Adjusted card title-selection logic to use a generation placeholder when status is pending and no parsed/derived title exists.",
      "Added translation key `text_card_generating_title` for localized placeholder text."
    ]
  },
  {
    id: "text-card-title-fallback-from-story",
    timestamp: "2026-03-01T17:08:47+01:00",
    title: "Text cards now derive a title from story when model title is absent",
    summary:
      "Newly generated texts now still show a strong header title even when the model output does not contain an explicit `title` field.",
    userHighlights: [
      "Cards no longer fall back to showing only the prompt as the main heading for new entries.",
      "The original prompt remains visible as secondary metadata when different from the displayed title.",
      "Title hierarchy remains consistent across standard and podcast cards."
    ],
    technicalNotes: [
      "Added client-side title derivation from the first meaningful story lines as fallback after explicit title parsing.",
      "Display logic now uses: parsed model title -> derived story title -> prompt topic."
    ]
  },
  {
    id: "text-card-ai-title-primary-prompt-secondary",
    timestamp: "2026-03-01T17:05:31+01:00",
    title: "Text cards now prioritize AI title over prompt",
    summary:
      "Generated text cards now show the AI-derived title as the main header and render the original user prompt/topic in a smaller secondary line.",
    userHighlights: [
      "Card header now emphasizes the generated title for clearer scanning.",
      "Your original prompt/topic is still visible but visually lighter and less dominant.",
      "Preview text stays visible under the new title hierarchy."
    ],
    technicalNotes: [
      "Added title extraction from strict JSON, loose JSON-like payloads, and `Title:` lines directly in card rendering.",
      "Applied the same title/prompt hierarchy to both standard and podcast text-card layouts, including the tilted preview card."
    ]
  },
  {
    id: "generated-text-json-wrapper-cleanup",
    timestamp: "2026-03-01T17:03:55+01:00",
    title: "Generated texts now strip JSON-like wrapper content",
    summary:
      "Texts that arrive in `title/story` JSON-like format are now normalized so only the story body is shown in the text view.",
    userHighlights: [
      "Raw payload fragments like `\"title\": ... \"story\": ...` no longer appear in generated text output.",
      "Existing affected texts also render cleaner in text details via frontend normalization.",
      "Title extraction still works when provided by the model."
    ],
    technicalNotes: [
      "Backend completion now normalizes strict JSON, fenced JSON, and loose JSON-like key/value outputs before persisting `chatgpt_output`.",
      "Text-details stream normalization now extracts `story` from loose JSON-like payloads for resilient rendering."
    ]
  },
  {
    id: "text-details-more-grammar-toggle-reliability",
    timestamp: "2026-03-01T16:58:35+01:00",
    title: "Text details: More grammar toggle now reacts reliably",
    summary:
      "The More grammar action on text-details vocabulary cards now toggles reliably and is only shown when extra grammar content exists.",
    userHighlights: [
      "The card no longer treats grammar-button clicks as jump-to-text clicks.",
      "More grammar appears only when there is additional grammar to expand.",
      "No-change clicks are avoided for entries that only have basic grammar fields."
    ],
    technicalNotes: [
      "Added explicit click-control guards in the text-details vocabulary item to prevent parent row jump handling from interfering with control interactions.",
      "Conditionally render expanded grammar block and toggle based on actual advanced grammar availability."
    ]
  },
  {
    id: "vocabulary-source-sentence-keeps-ending-punctuation",
    timestamp: "2026-03-01T16:55:19+01:00",
    title: "Vocabulary source sentence now keeps full ending punctuation",
    summary:
      "Saved source sentences now include terminal punctuation like `.`, `!`, or `?` when a word is added from text selection.",
    userHighlights: [
      "Source sentence lines now keep their full ending, including punctuation marks.",
      "This avoids clipped sentence context in vocabulary preview and detail cards.",
      "Existing sentence order and styling remain unchanged."
    ],
    technicalNotes: [
      "Backend `add_vocabulary` sentence extraction now extends to terminal punctuation and trailing closing quote/bracket characters when needed.",
      "Added regression test to ensure `sentence_end_index` selections still persist the full sentence ending."
    ]
  },
  {
    id: "text-details-vocab-card-layout-aligned",
    timestamp: "2026-03-01T16:52:29+01:00",
    title: "Text details vocabulary card now matches preview layout",
    summary:
      "The vocabulary card on text details now follows the same visual sectioning as the preview card for sentence and grammar content.",
    userHighlights: [
      "Sentence lines are shown in their own card section with the same spacing and typography pattern.",
      "Grammar is shown in a separate card section with consistent More grammar placement and styling.",
      "Existing behavior (prefix removal, source-word bolding, source-first order) remains intact."
    ],
    technicalNotes: [
      "Restructured `VocabularyItem` in text details to use distinct sentence and grammar containers aligned with preview-card design language.",
      "No backend/API changes."
    ]
  },
  {
    id: "srs-includes-manual-vocabulary",
    timestamp: "2026-03-01T16:49:02+01:00",
    title: "SRS trainer and stage cards now include manual vocabulary",
    summary:
      "Manual vocabulary is now counted in SRS trainer selection and stage cards, so language-space practice reflects your full vocabulary.",
    userHighlights: [
      "Vocabulary preview no longer says there is nothing to practice when manual words exist.",
      "Stage cards (Due/New/Learning/Reviewing/Mastered) now reflect manual words in the selected language space.",
      "Behavior is consistent with the vocabulary list users already see."
    ],
    technicalNotes: [
      "Backend recent-vocabulary helper now includes manual-origin words in addition to usage-linked/photo-import words.",
      "Impacts both `/vocabulary/top_fifteen_priority/` and `/vocabulary/stage_counts/` via shared filtering."
    ]
  },
  {
    id: "preview-sentences-below-self-rating",
    timestamp: "2026-03-01T16:39:40+01:00",
    title: "Preview card sentences moved below self-rating buttons",
    summary:
      "On the vocabulary preview card, source/translated sentence lines now appear under the Yes/Almost/No self-rating controls.",
    userHighlights: [
      "Sentence section is now positioned below the `Did you know the word?` button row.",
      "Sentence content and highlighting behavior remain unchanged.",
      "Grammar section still stays below the self-rating section."
    ],
    technicalNotes: [
      "Reordered sentence block placement in `VocabularyPreviewCard` without API or schema changes."
    ]
  },
  {
    id: "preview-vocabulary-pending-translation-refetch",
    timestamp: "2026-03-01T16:37:00+01:00",
    title: "Preview vocabulary now refreshes pending translations automatically",
    summary:
      "Newly added words in vocabulary preview no longer get stuck on the temporary generated placeholder.",
    userHighlights: [
      "If a word is still generating translation, preview auto-refreshes until final text arrives.",
      "Preview behavior now aligns with text-details vocabulary updates.",
      "The temporary `currently beeing generated` text no longer lingers after translation completes."
    ],
    technicalNotes: [
      "Added conditional polling to the preview trainer vocabulary query while any entry is pending or still carries generation-placeholder text.",
      "No backend/API changes."
    ]
  },
  {
    id: "vocabulary-sentence-left-align-no-source-bg",
    timestamp: "2026-03-01T16:33:23+01:00",
    title: "Vocabulary sentences are left-aligned and source background removed",
    summary:
      "Sentence lines are now explicitly left-aligned and the shaded background from the first sentence line has been removed.",
    userHighlights: [
      "Source sentence line no longer uses a gray background block.",
      "Both sentence lines are consistently left-aligned.",
      "Content and ordering of sentence lines stay the same."
    ],
    technicalNotes: [
      "Updated sentence line classes in preview, trainer, generated-text card, and vocabulary progress (desktop + mobile).",
      "No backend or API changes."
    ]
  },
  {
    id: "vocabulary-source-sentence-word-highlight",
    timestamp: "2026-03-01T16:31:47+01:00",
    title: "Vocabulary word is now bold in source sentence",
    summary:
      "The vocabulary word is now visually emphasized in the source sentence line to improve recognition while reviewing.",
    userHighlights: [
      "In the first sentence line (source sentence), the vocabulary word is bold.",
      "Highlighting appears consistently in preview, trainer, generated-text vocabulary cards, and vocabulary progress.",
      "If source sentence is missing, fallback text remains unchanged."
    ],
    technicalNotes: [
      "Added a shared sentence-highlighting utility and applied it to source sentence rendering across vocabulary surfaces.",
      "No backend/API changes."
    ]
  },
  {
    id: "vocabulary-source-sentence-first-order",
    timestamp: "2026-03-01T16:30:05+01:00",
    title: "Vocabulary sentence section now always starts with source sentence",
    summary:
      "Sentence sections now use fixed order again: source sentence (or fallback) first, translated sentence second.",
    userHighlights: [
      "The source sentence slot is always shown first in the section.",
      "If source sentence is missing, `Kein Ausgangssatz verfügbar.` appears first.",
      "Translated sentence still appears as the second line when available."
    ],
    technicalNotes: [
      "Replaced first-available sentence logic with strict source-first ordering across all vocabulary surfaces.",
      "No backend or API changes."
    ]
  },
  {
    id: "vocabulary-sentence-labels-removed",
    timestamp: "2026-03-01T16:28:39+01:00",
    title: "Vocabulary cards now show sentence text without prefixes",
    summary:
      "Sentence lines no longer display `Ausgangssatz:` or `Übersetzter Satz:` prefixes; only the sentence text is shown.",
    userHighlights: [
      "Cleaner sentence presentation across vocabulary cards and progress views.",
      "Primary and secondary sentence ordering remains unchanged.",
      "Fallback text still appears when no sentence is available."
    ],
    technicalNotes: [
      "Removed sentence label rendering while keeping sentence-priority logic.",
      "Applied in preview, trainer, generated-text vocabulary card, and vocabulary progress (desktop + mobile)."
    ]
  },
  {
    id: "vocabulary-preview-grammar-below-self-rating",
    timestamp: "2026-03-01T16:26:33+01:00",
    title: "Vocabulary preview: grammar moved below self-rating actions",
    summary:
      "In the preview trainer card, the grammar block now appears below the `Did you know the word?` Yes/Almost/No actions.",
    userHighlights: [
      "Self-rating buttons now appear before grammar details in the preview flow.",
      "Sentence context remains visible above, so reading context still comes first.",
      "Grammar expand/collapse behavior is unchanged; only placement changed."
    ],
    technicalNotes: [
      "Reordered the preview card layout so the grammar section renders after the self-rating action section.",
      "No backend or API changes."
    ]
  },
  {
    id: "vocabulary-primary-sentence-priority",
    timestamp: "2026-03-01T16:25:10+01:00",
    title: "Vocabulary cards prioritize the primary learning sentence",
    summary:
      "Sentence context now highlights the first available real sentence as primary, so placeholder text does not appear before useful content.",
    userHighlights: [
      "When `Ausgangssatz` is unavailable but a sentence exists in `Übersetzter Satz`, the real sentence is shown first.",
      "The primary sentence is visually emphasized to reflect higher learning importance.",
      "The unavailable placeholder is only shown when no sentence exists at all."
    ],
    technicalNotes: [
      "Introduced primary/secondary sentence ordering logic in preview, trainer, generated-text vocabulary cards, and vocabulary progress (desktop + mobile).",
      "Applied stronger styling to the primary sentence row and kept existing grammar separation behavior."
    ]
  },
  {
    id: "vocabulary-translated-sentence-double-label-fix",
    timestamp: "2026-03-01T16:20:50+01:00",
    title: "Vocabulary cards: duplicate translated-sentence label removed",
    summary:
      "Translated sentence values no longer show duplicated labels like `Übersetzter Satz: Satzübersetzung: ...`.",
    userHighlights: [
      "Translated sentence lines now render once with a single label.",
      "Legacy entries that stored `Satzübersetzung:` inside the value are cleaned automatically in the UI.",
      "No data migration required to see the fix."
    ],
    technicalNotes: [
      "Added normalization that strips known translated-sentence prefixes (`translated sentence`, `übersetzter satz`, `satzübersetzung`) from the value.",
      "Applied to both structured `translation_details.translated_sentence` and legacy newline fallback parsing."
    ]
  },
  {
    id: "vocabulary-more-grammar-plain-toggle",
    timestamp: "2026-03-01T16:19:58+01:00",
    title: "Vocabulary grammar toggle simplified to plain text",
    summary:
      "The More grammar control now uses a plain text button without chevron or outline styling.",
    userHighlights: [
      "Removed chevron icons from the grammar expand/collapse control.",
      "Removed outline/border styling from the same control for a cleaner look.",
      "Grammar content order and expand/collapse behavior remain unchanged."
    ],
    technicalNotes: [
      "Dropped `ChevronDownIcon` and `ChevronUpIcon` imports/usages in all vocabulary surfaces.",
      "Adjusted button classes to plain text styling while preserving toggle logic."
    ]
  },
  {
    id: "vocabulary-more-grammar-chevron",
    timestamp: "2026-03-01T16:18:29+01:00",
    title: "Vocabulary grammar toggle now shows chevron state",
    summary:
      "The More grammar toggle now includes a chevron icon that clearly indicates expand and collapse state.",
    userHighlights: [
      "A down chevron appears with `More grammar`; an up chevron appears with `Less grammar`.",
      "The icon behavior is consistent across preview, trainer, generated-text cards, and vocabulary progress.",
      "Existing field order and grammar content remain unchanged."
    ],
    technicalNotes: [
      "Added Heroicons `ChevronDownIcon`/`ChevronUpIcon` to all More grammar buttons.",
      "No backend or API changes; this is a frontend affordance update."
    ]
  },
  {
    id: "vocabulary-more-grammar-collapsed-reset",
    timestamp: "2026-03-01T16:15:26+01:00",
    title: "Vocabulary cards: More grammar reset to collapsed state",
    summary:
      "More grammar now starts collapsed again across vocabulary surfaces while preserving the new sentence-first content order.",
    userHighlights: [
      "Expanded grammar no longer stays open from older saved preference.",
      "Source sentence and translated sentence remain above grammar fields.",
      "You can still open and close advanced grammar manually with the same button."
    ],
    technicalNotes: [
      "Bumped the localStorage key for the grammar toggle from v1 to v2 to reset persisted expanded state.",
      "No API or schema changes; this is a frontend preference reset only."
    ]
  },
  {
    id: "vocabulary-sentences-before-grammar",
    timestamp: "2026-03-01T16:13:53+01:00",
    title: "Vocabulary cards: sentence context moved above grammar",
    summary:
      "Vocabulary cards now show source and translated sentence context before grammar fields like part of speech and tense.",
    userHighlights: [
      "You now see `Ausgangssatz` and (if available) `Übersetzter Satz` before the grammar section.",
      "The fallback `Kein Ausgangssatz verfügbar.` remains in place when no source sentence exists.",
      "Order is consistent across preview, trainer, generated-text cards, and vocabulary progress."
    ],
    technicalNotes: [
      "Reordered sentence and grammar blocks in all vocabulary UI surfaces without changing data contracts.",
      "More grammar toggle behavior is unchanged; only content order was updated."
    ]
  },
  {
    id: "vocabulary-more-grammar-toggle-bottom",
    timestamp: "2026-03-01T16:11:26+01:00",
    title: "Vocabulary cards: More grammar moved to the bottom",
    summary:
      "The More grammar button is now placed at the bottom of vocabulary detail blocks for a clearer expand/collapse flow.",
    userHighlights: [
      "More grammar/Less grammar now appears at the bottom of vocabulary detail sections instead of near the top.",
      "Expanded grammar details keep the same content and behavior; only placement changed for readability.",
      "The new placement is consistent across preview, trainer, generated-text cards, and vocabulary progress."
    ],
    technicalNotes: [
      "Reordered the grammar toggle markup to render after grammar and sentence details in all vocabulary surfaces.",
      "No API or data changes; this is a frontend layout adjustment only."
    ]
  },
  {
    id: "source-sentence-unavailable-copy",
    timestamp: "2026-03-01T16:07:21+01:00",
    title: "Vocabulary source sentence fallback copy refined",
    summary:
      "The source sentence fallback label now uses neutral wording and no longer says “missing”.",
    userHighlights: [
      "Cards and lists now show “No source sentence available.” when no source sentence exists.",
      "The fallback remains clearly labeled as Source sentence for learning context.",
      "Wording is now consistent across preview, trainer, progress, and text-detail vocabulary cards."
    ],
    technicalNotes: [
      "Renamed the translation key to `vocabulary_source_sentence_unavailable` and updated all frontend references.",
      "No behavioral change besides copy/key naming; fallback display logic remains the same."
    ]
  },
  {
    id: "vocabulary-progress-grammar-separation",
    timestamp: "2026-03-01T16:01:30+01:00",
    title: "Vocabulary progress: separated grammar layout added",
    summary:
      "The vocabulary progress page now uses the same separated translation/grammar structure and More grammar toggle as other vocabulary surfaces.",
    userHighlights: [
      "Progress table and mobile cards now show translation and grammar as separate readable values.",
      "Core grammar remains visible by default; advanced grammar is shown with `More grammar`.",
      "Source and translated example sentences are shown as separate labeled lines."
    ],
    technicalNotes: [
      "Vocabulary progress rows now normalize `translation_details` and safely fall back to legacy newline-based translation strings.",
      "Integrated shared localStorage-backed `More grammar` toggle key for consistent behavior across surfaces."
    ]
  },
  {
    id: "vocabulary-grammar-separated-ui",
    timestamp: "2026-03-01T15:58:08+01:00",
    title: "Vocabulary cards: grammar fields split with a More grammar toggle",
    summary:
      "Vocabulary surfaces now separate translation details into readable fields and add a shared More grammar toggle for advanced details.",
    userHighlights: [
      "Preview card, text-detail vocabulary card, and vocabulary list now show translation and grammar in separate blocks.",
      "Core grammar stays visible by default; advanced grammar opens via `More grammar`.",
      "The toggle preference is remembered locally, so the view stays consistent while learning."
    ],
    technicalNotes: [
      "Added a shared frontend translation-details normalizer that reads structured payloads and safely falls back to legacy newline translation strings.",
      "Extended `VocabularyDto` with optional `translation_details` and migrated UI rendering in three vocabulary surfaces to separated fields."
    ]
  },
  {
    id: "generated-text-enqueue-failure-handling",
    timestamp: "2026-03-01T15:28:57+01:00",
    title: "Generate Text: failed queue jobs no longer create silent empty cards",
    summary:
      "When text generation cannot be queued, the request now completes safely and marks the new text as failed instead of leaving a silent empty pending card.",
    userHighlights: [
      "Creating a text no longer crashes when the background queue is temporarily unavailable.",
      "Failed generations are now clearly marked as failed instead of looking stuck.",
      "You can retry immediately instead of guessing whether generation is still running."
    ],
    technicalNotes: [
      "Backend wraps `complete_generated_text.apply_async(...)` in guarded error handling during `GeneratedText` creation.",
      "On enqueue failure, the text is persisted with `status=FAILED` and a user-visible fallback message instead of returning a server error."
    ]
  },
  {
    id: "playwright-e2e-make-targets",
    timestamp: "2026-02-27T21:17:53Z",
    title: "E2E tests: make targets for headed/headless runs",
    summary:
      "Playwright runs are now available via make targets, with a headed mode for interactive debugging.",
    userHighlights: [
      "Use `make e2e` to run tests with a visible browser.",
      "Use `make e2e-headless` for fast CI-style runs."
    ],
    technicalNotes: [
      "Makefile now exposes two Playwright targets for headed and headless execution."
    ]
  },
  {
    id: "secret-signup-inactive-users",
    timestamp: "2026-02-27T20:14:04Z",
    title: "Secret signup: inactive accounts by default",
    summary:
      "A private signup page lets invited users create accounts that remain inactive until manually approved.",
    userHighlights: [
      "Simple username + password signup at `/secretsignup`.",
      "Clear success screen explains activation within 24 hours.",
      "No login until the account is manually activated."
    ],
    technicalNotes: [
      "New API endpoint creates users with `is_active = false` and runs Django password validation.",
      "Signup does not log the user in; activation remains manual."
    ]
  },
  {
    id: "command-bar-smart-menu",
    timestamp: "2026-02-27T19:31:00Z",
    title: "Command bar: smart menu for fast actions",
    summary:
      "The burger menu is now a smart command bar that understands natural-language requests and still keeps the full side nav available.",
    userHighlights: [
      "Type what you need (e.g., change language, switch model, start daily text) and confirm in one tap.",
      "Direct actions first: change settings or open the photo picker without hunting through screens.",
      "Side navigation stays visible below the command bar so nothing is lost."
    ],
    technicalNotes: [
      "Command bar parses German/English intents and executes settings updates, daily text creation, and profile photo uploads.",
      "Action-specific confirmations replace generic “Apply” for clearer UX.",
      "Mobile uses a full-width sheet with the command bar pinned at the top."
    ]
  },
  {
    id: "text-details-vocab-focus-highlight",
    timestamp: "2026-02-22T20:57:55Z",
    title: "Text details: highlight selected vocab and jump to words",
    summary:
      "Words chosen for “new text from vocabulary” are now highlighted in the text, and tapping a vocab card scrolls to that word with a short flash.",
    userHighlights: [
      "Selected vocab words are highlighted directly in the generated text, matching the click-to-highlight style.",
      "Tapping a vocab card scrolls the text to that word and briefly highlights it.",
      "Unselecting a word removes its highlight so you can see what will be used next."
    ],
    technicalNotes: [
      "Detail view now passes selected vocab words to the text renderer for highlight indexing.",
      "Text span rendering adds data attributes per word and supports a timed flash highlight with smooth scroll."
    ]
  },
  {
    id: "daily-text-on-demand",
    timestamp: "2026-02-01T15:10:00Z",
    title: "Daily text: on-demand card with stable vocab picks",
    summary:
      "A 3pm callout lets you create a daily text per language using your due vocab and last generator settings. The text appears as a normal card; no clutter if you skip it.",
    userHighlights: [
      "At 15:00 you see a daily-text button; tapping creates today’s text for the current language and shows it in the list.",
      "Uses the same word queue you’re struggling with; if nothing is due, you’re told why.",
      "No duplicate cards: if you’ve already created today’s daily text, the callout stays hidden."
    ],
    technicalNotes: [
      "Backend endpoint `/generated_texts/daily-practice/` now reuses any existing daily text per user/language/day and triggers generation on click.",
      "Frontend callout is 3pm-gated, language-space aware, CSRF-protected, and invalidates the paginated text list after creation.",
      "Translations consolidated into `dailyText*` keys; no inline fallbacks."
    ]
  },
  {
    id: "knowledge-style-adult-tone",
    timestamp: "2026-02-05T09:30:00Z",
    title: "Knowledge text style: adult, factual output at all levels",
    summary:
      "Knowledge texts now stay factual and adult in tone, with A0–A2 guidance to avoid childlike language while keeping sentences simple.",
    userHighlights: [
      "Pick the new “Knowledge” style to get fact-based texts about your topic, not fiction.",
      "Beginner levels stay simple but avoid babyish wording; short sentences use clear connectors and adult contexts.",
      "Higher levels remain concise and factual, with no speculative or fluffy phrasing."
    ],
    technicalNotes: [
      "Backend Knowledge prompt adds adult tone and factual constraints, plus A0–A2-specific guidance: short, connected sentences, no cutesy/filler language, everyday adult examples.",
      "No schema changes; frontend already exposes the Knowledge type in the generator."
    ]
  },
  {
    id: "knowledge-no-main-character",
    timestamp: "2026-02-05T10:00:00Z",
    title: "Knowledge texts: no main character, narratives keep yours",
    summary:
      "Knowledge texts now ignore main characters, while Story/Dialog/Crime/Podcast remember your last protagonist per language. Switching to Knowledge won’t wipe it.",
    userHighlights: [
      "Knowledge text type disables the main-character field and sends none.",
      "When you return to other types, your last chosen character is prefilled (per language).",
      "You can still clear it; Knowledge won’t overwrite your saved choice."
    ],
    technicalNotes: [
      "Frontend: main-character input is disabled for Knowledge; submissions send an empty value; stored defaults preserve the last non-Knowledge character per language.",
      "Backend: Knowledge texts force an empty main_character before generation."
    ]
  },
  {
    id: "in-app-language-helper-mvp",
    timestamp: "2026-02-20T11:00:00Z",
    title: "In-app helper: ask, answer, and add vocab",
    summary:
      "A lightweight helper inside text details answers questions and lets you add words and example sentences directly to your vocabulary.",
    userHighlights: [
      "Chat-style helper with a single input; Enter sends on desktop.",
      "Asks for quick confirmation before adding to vocabulary so you see exactly what will be saved.",
      "Answers in the learning language for sentence/translation requests; explanations stay in your mother tongue.",
    ],
    technicalNotes: [
      "Helper uses first-question context from the generated text plus the last 5 clicked words.",
      "Agent responses follow intent rules (sentence, pronunciation, register, grammar).",
      "Uxhulu voice prompt enforces a calm editorial tone with bilingual few-shot examples.",
      "Intent routing keeps sentence/translation replies in the learning language; other answers stay in mother tongue.",
      "Agent can infer the vocab word from the last assistant phrase when the user asks to add it.",
      "New vocab additions go through the standard translation pipeline to preserve multi-line analysis.",
    ],
  },
  {
    id: "trainer-similar-words-spelling",
    timestamp: "2026-02-15T10:26:27+01:00",
    title: "Trainer: similar words by spelling",
    summary:
      "Expanded trainer cards now show spelling-similar words from your current deck, so you can jump to near matches without leaving the session.",
    userHighlights: [
      "See up to 5 near-spelling words in the expanded trainer card under “Ähnliche Wörter”.",
      "Tap a suggestion to switch the active card instantly.",
      "Only uses your current batch and language; no duplicate exact matches."
    ],
    technicalNotes: [
      "Client-side accent-insensitive Levenshtein on the current deck; dist ≤ 2, normalized score ≥ 0.6; top 5.",
      "UI: single tab “Schreibweise”, scrollable list, reason label “sehr ähnlich geschrieben”."
    ]
  },
  {
    id: "multi-language-spaces-stable-queues",
    timestamp: "2026-01-30T10:00:00Z",
    title: "Language spaces: per-language defaults and stable queues",
    summary:
      "Each language now has its own generator defaults and trainer queue. Switching spaces or refreshing keeps your current word and form selections intact.",
    userHighlights: [
      "Generate Text remembers level, tense, type, word count, and main character separately per language.",
      "Vocabulary preview shows a stable queue per language—even after switching spaces or refreshing, the same first word stays until you act on it.",
      "Language switcher supports all languages with distinct accents and mobile-friendly swipe/tap navigation."
    ],
    technicalNotes: [
      "Per-language form defaults stored in localStorage keyed by language; generator hydrates on space change.",
      "Vocabulary preview persists batch snapshots and restores them per language before API data to prevent reorder on refresh.",
      "Language space selection options driven from TEXT_LANGUAGES; mobile pill bar uses per-language accent styling."
    ]
  },
  {
    id: "trainer-session-size-picker",
    timestamp: "2026-01-20T15:20:00Z",
    title: "Trainer sessions: choose your round size",
    summary:
      "You can now pick how many words are in a round (5–50) from the “continue?” prompt, without changing your due list.",
    userHighlights: [
      "Rounds stay a fixed size; when you finish, tap the menu to choose a different size for the next round.",
      "Words remain stable within a round—no mid-session swaps even if you change the size later.",
      "Settings persist locally, so your preferred round size sticks when you come back."
    ],
    technicalNotes: [
      "Frontend adds a session-size menu on the bundle-done prompt, stores selection in localStorage, and reinitializes batches with the new size.",
      "Batching continues to use frozen IDs per round; only the slice size changes for subsequent rounds."
    ]
  },
  {
    id: "trainer-5-word-batches",
    timestamp: "2026-01-20T12:30:00Z",
    title: "Trainer: 5-word sessions with clear breaks",
    summary:
      "The vocabulary trainer now serves due words in fixed sets of five, then asks if you want to continue—no more endless queues without a breakpoint.",
    userHighlights: [
      "You always see a stable set of five due words; new words don’t slip in mid-session.",
      "After five answers, you get a calm “continue?” prompt to start the next five.",
      "If you leave and return, the trainer restarts at the first five due words instead of resuming mid-stream."
    ],
    technicalNotes: [
      "Frontend freezes a per-session batch (SESSION_SIZE=5), tracks answeredCount, and shows the bundle-done state only after all five are answered.",
      "Batch state resets cleanly on refresh or language filter changes; continuation pulls the next due words without altering the server queue."
    ]
  },
  {
    id: "podcast-audio-two-voices",
    timestamp: "2026-01-08T15:50:00Z",
    title: "Podcasts: two voices, intro/outro, reliable playback",
    summary:
      "Podcast episodes now sound like real shows: two distinct voices with a short intro/outro, leveled audio, cleaner titles, and playback that works on desktop and mobile.",
    userHighlights: [
      "Two distinct voices alternate naturally with a brief podcast-style welcome and wrap-up.",
      "Titles are generated from the episode content—no generic greetings or quotes.",
      "Podcast cards play reliably via the new speech endpoint on web and mobile."
    ],
    technicalNotes: [
      "Backend: multi-voice TTS with loudness normalization, silence gaps, intro/outro prompt tweaks, and a dedicated /speech action; serializer returns absolute media URLs.",
      "Frontend: podcast card handles audio more robustly with hidden native controls and error handling.",
      "Title generation: prompt and post-process to produce concise, content-based headlines instead of first-line greetings."
    ]
  },
  {
    id: "podcast-text-type",
    timestamp: "2025-12-12T09:00:00Z",
    title: "Podcast text type with audio",
    summary:
      "You can now pick “Podcast” when generating content: a 2-speaker, ~1-minute script is written from your topic and runs through text-to-speech so you can listen back.",
    userHighlights: [
      "New “Podcast” option alongside Story/Dialog/Krimi in the generator.",
      "Script uses your topic and supplied words, keeps two voices, and stays concise (~1 minute).",
      "Audio is generated so you can replay the episode from its card."
    ],
    technicalNotes: [
      "Backend adds PODCAST text type, tailored prompt, and podcast-friendly TTS instructions.",
      "Frontend exposes the new type in the generator; choices and migrations updated."
    ]
  },
  {
    id: "vocab-progress-fast-counts",
    timestamp: "2025-12-11T22:10:00Z",
    title: "Vocabulary progress shows live counts instantly",
    summary:
      "Stage cards now load their counts directly from the server instead of slowly accruing as you scroll, so you see the real numbers upfront.",
    userHighlights: [
      "Fällig/Lernen/Review counts appear immediately without waiting for list pagination.",
      "Photo-import vocab still has its own tab, but is included in the main counts when you’re on “Alle”."
    ],
    technicalNotes: [
      "Backend adds a `stage_counts` action aligned to trainer filters (language/origin/max streak/text age) and SRS delays to return counts in one request.",
      "Frontend uses the stage-counts hook for stage badges (client reduction as fallback) and applies the correct language filter even for photo imports."
    ]
  },
  {
    id: "photo-vocab-highlight-coverage-4",
    timestamp: "2025-12-11T18:20:00Z",
    title: "Photo vocab import: sharper highlights, zero-drop fallback",
    summary:
      "Image preprocessing now boosts contrast/sharpness for faint marks, and the extractor is forced to return at least as many entries as highlights it counts.",
    userHighlights: [
      "Faint highlights are clearer to the model thanks to contrast and sharpness boosts.",
      "If the model is unsure, it still returns a low-confidence entry rather than skipping a highlight.",
      "One entry per distinct highlight remains—phrases stay intact, separate marks stay separate."
    ],
    technicalNotes: [
      "Image pipeline adds autocontrast + mild contrast/sharpness enhancement before resize (2000px, LANCZOS).",
      "Prompt now requires the model to verify its entry count >= counted highlights and to over-include with low confidence if uncertain."
    ]
  },
  {
    id: "photo-vocab-translation-grammar",
    timestamp: "2025-12-11T18:40:00Z",
    title: "Photo vocab translations: enforce grammatical sentences",
    summary:
      "Translations now ask the model to fix agreement/tense and avoid literal calques, with deterministic decoding to reduce odd phrasing.",
    userHighlights: [
      "Sentence translations aim to be grammatical and idiomatic instead of literal word-for-word copies.",
      "Lower randomness reduces quirky outputs when translating highlighted sentences."
    ],
    technicalNotes: [
      "Translation prompt now explicitly repairs agreement/tense/prepositions while staying faithful to meaning.",
      "Temperature fixed at 0 and max_tokens capped for stable, deterministic outputs."
    ]
  },
  {
    id: "photo-vocab-highlight-coverage-3",
    timestamp: "2025-12-11T15:10:00Z",
    title: "Photo vocab import: one entry per highlight, no merges or drops",
    summary:
      "Each distinct highlight now returns exactly one entry in reading order—even multi-word spans stay intact—with added safeguards against accidental omissions.",
    userHighlights: [
      "One entry per mark: if you highlight a phrase, it stays together; separate highlights stay separate.",
      "Zero-drop policy remains: faint or hard-to-read marks still show up with low confidence instead of vanishing.",
      "Higher max tokens in the vision reply avoid truncated lists on dense pages."
    ],
    technicalNotes: [
      "Highlight mode no longer splits spans; it keeps the marked text as-is with a deterministic token_id and ordered results, and asks the model to count highlights then return at least that many entries.",
      "Prompt explicitly forbids merging distinct highlights and keeps the over-inclusion safety net; vision max_tokens at 2600.",
      "Image preprocessing continues to use high-res autocontrast pipeline for clearer marks."
    ]
  },
  {
    id: "photo-vocab-highlight-articles",
    timestamp: "2025-12-11T17:05:00Z",
    title: "Photo vocab import: keep articles inside highlights",
    summary:
      "Highlighted phrases are kept verbatim—including articles and prepositions—so marks like “le rétroviseur” stay intact instead of being shortened.",
    userHighlights: [
      "Marked spans are returned exactly as highlighted, preserving articles/determiners when they’re underlined or colored.",
      "Still one entry per distinct highlight with zero-drop behavior on faint marks."
    ],
    technicalNotes: [
      "Highlight prompt now explicitly preserves the full marked span (no head-noun shortening) while maintaining the count-and-include safeguard.",
      "No changes to image pipeline or token limits; applies only to extraction rules."
    ]
  },
  {
    id: "photo-vocab-highlight-coverage-2",
    timestamp: "2025-12-11T12:30:00Z",
    title: "Photo vocab import: zero-drop for highlighted words",
    summary:
      "Highlighted-only imports now insist on returning every marked word, even tiny prepositions or faint strokes, and keep higher-resolution images for the model.",
    userHighlights: [
      "Marked words are no longer skipped: underlined/highlighted spans are split into individual words and included even when contrast is low.",
      "Tiny two-letter words that are marked are kept instead of being filtered out.",
      "Higher-res uploads (up to 2000px) give the model clearer text for consistent extractions."
    ],
    technicalNotes: [
      "Prompt now forbids omission, requires per-word outputs for multi-word highlights, includes short words, and removes implicit count limits.",
      "Max tokens for the vision response raised to avoid truncation on longer sentences; temp remains 0 for determinism.",
      "Image preprocessing keeps aspect ratio and bumps max dimension to 2000px with LANCZOS to preserve fine annotations."
    ]
  },
  {
    id: "photo-vocab-highlight-coverage",
    timestamp: "2025-12-11T08:00:00Z",
    title: "Photo vocab import keeps every highlighted word",
    summary:
      "Uploads that contain highlighted or underlined words now return the full set reliably, even when the handwriting is faint or the lighting is uneven.",
    userHighlights: [
      "Highlighted-only imports no longer drop random words; the extractor returns every marked token instead of a partial list.",
      "Faint or low-contrast marks are still included with a low-confidence flag instead of being skipped.",
      "Keeping the original aspect ratio and higher resolution preserves tiny annotations and pen underlines."
    ],
    technicalNotes: [
      "Vision prompt now explicitly forbids skipping marked words and asks for best-effort guesses with low confidence instead of omission.",
      "Image preprocessing retains aspect ratio at up to 1400px using LANCZOS downscaling, removes destructive in-place overwrite, and encodes JPEG at higher quality.",
      "These changes make repeated uploads deterministic and keep all marked tokens available for selection."
    ]
  },
  {
    id: "struggle-words-practice-texts",
    timestamp: "2025-12-07T09:10:00Z",
    title: "Practice texts for tricky words",
    summary:
      "Struggling vocabulary now triggers a dedicated practice text: we pick recent hard words, generate a short title and story, highlight the words in context, and surface a distinct card to revisit it.",
    userHighlights: [
      "A new banner offers an “Übungstext” when you have recent struggling words; after generation it hides for a day.",
      "The practice text card appears in your main list with a subtle badge, so you can always return to it.",
      "On the text detail page, the exact struggling words used are listed and highlighted in the text."
    ],
    technicalNotes: [
      "Backend: `GeneratedText` stores `struggle_words`; generation pulls up to 8 low-streak vocab (last 30 days) in the selected language, injects them into the prompt, and derives a short title from the body.",
      "Async generation no longer hits the ORM; all struggle vocab is gathered synchronously, fixing SynchronousOnlyOperation errors and avoiding empty texts.",
      "Frontend: practice-text banner fetches struggle words daily and hides for 24h after use; detail view highlights the stored struggle words (filtered by text language); main cards show an “Übungstext” badge without special borders; form selections are remembered for the next practice text."
    ]
  },
  {
    id: "photo-vocab-auto-translation",
    timestamp: "2025-12-05T13:00:00Z",
    title: "Photo vocab import: smarter translations & tags",
    summary:
      "Photo imports now auto-route translations per token, show the learning-language word with the mother-tongue translation, and land you on the photo tab after import.",
    userHighlights: [
      "Words extracted from photos show the learning-language term in “Wort” with the mother-tongue translation (and grammar info) in “Übersetzung”.",
      "Language badges reflect the actual source (e.g., French stays French); mixed-language imports can still be previewed before import.",
      "After finishing an import, the vocab list opens directly on the “Photo” tab."
    ],
    technicalNotes: [
      "Finalize/preview auto-select targets per token (mother tongue vs. learning language), normalize language codes, and store target vs. source consistently.",
      "Vocabulary DTOs expose translation_language; the UI filters/badges via word_language for correct tagging.",
      "Success link carries ?tab=photo so the vocab list opens on the photo-import view."
    ]
  },
  {
    id: "photo-vocabulary-import-ui",
    timestamp: "2024-05-28T12:00:00Z",
    title: "Photo vocabulary import (UX workflow)",
    summary:
      "Uploading a picture now launches a guided import flow: you choose whether to write a story or extract vocabulary, watch the AI read the photo, and cherry-pick the detected words before they land in a dedicated list.",
    userHighlights: [
      "Right after selecting a photo you decide between “Generate text” and “Import vocabulary,” so stories are never triggered by accident.",
      "The import view shows a real-time status card, each detected word with its sentence, and tools to select all, clear, or skip entries.",
      "Imported words appear in the new “Photo imports” tab inside the vocabulary list, making them easy to revisit.",
    ],
    technicalNotes: [
      "GenerateTextCard now polls the `/photo_vocabulary_imports/` endpoints, handles intent modals, status states, and lets users finalize token IDs.",
      "Result payloads are normalized client-side (deterministic token IDs, per-word sentence/language) before batching a finalize call.",
      "The vocabulary list API/UI supports an `origin=PHOTO_IMPORT` filter, exposed via a tab that reuses the infinite-query pipeline.",
    ],
  },
  {
    id: "photo-vocabulary-import-backend",
    timestamp: "2024-05-28T10:00:00Z",
    title: "Photo vocabulary import (backend groundwork)",
    summary:
      "Image uploads now branch into a dedicated vocabulary-import pipeline: the server extracts highlighted words or entire lists, tags the language, and serves them in a reviewable batch.",
    userHighlights: [
      "Uploading a picture asks whether you want to generate a story or import vocabulary, so no accidental text generations.",
      "Words imported from photos carry their original sentence context and appear in a dedicated list, making it easier to study book excerpts or word lists.",
      "Failed imports surface clear status/error messages so you know when the AI could not read a page.",
    ],
    technicalNotes: [
      "Backend adds `PhotoVocabularyImport` with Celery processing: OpenAI vision parses the photo, outputs JSON with word, sentence, language, and deterministic `token_id`.",
      "New REST endpoints let the client poll import status and finalize selected words; vocabulary entries now track `origin` and keep a pointer to the import batch.",
      "Translations work without `VocabularyUsage` thanks to a fallback that feeds the stored sentence directly into the translation task.",
    ],
  },
  {
    id: "spaced-repetition-response-time",
    timestamp: "2024-05-27T14:30:00Z",
    title: "Spaced Repetition respects thinking time",
    summary:
      "The trainer now records how long you think about a word before revealing it. Fast answers are rewarded; hesitant ones keep the word in a tougher state.",
    userHighlights: [
      "Only the time before the first tap is captured; you can take your time to pick Ja/Fast/Nein once the answer is visible.",
      "Ja: quick or steady answers advance the streak, slow clicks keep the word at the current difficulty.",
      "Fast and Nein push the word back sooner if you hesitated for a long time, focusing the queue on shaky vocabulary.",
    ],
    technicalNotes: [
      "Frontend: each card starts `responseTimer` on mount, locks it on the first tap, and sends `response_time_ms` with every Ja/Fast/Nein mutation.",
      "Backend: the DRF actions bucketize latency (≤2.5s, 2.5–8s, ≥8s) and mutate `winning_streak`/`last_tested` via helper functions.",
      "Effect: slow Ja responses stop incrementing the streak; slow Nein responses backdate `last_tested` more aggressively; fast Fast clicks leave streak untouched.",
    ],
  },
];

const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(iso));

function ReleaseCard(entry: ReleaseEntry) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
        <span>{formatDateTime(entry.timestamp)}</span>
        <span className="hidden sm:inline">•</span>
        <span>{entry.id}</span>
      </div>
      <h3 className="mt-2 text-lg font-semibold text-slate-900">
        {entry.title}
      </h3>
      <p className="mt-2 text-sm text-slate-600">{entry.summary}</p>
      <div className="mt-4 space-y-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            For users
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600 list-disc pl-5">
            {entry.userHighlights.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Technical details
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600 list-disc pl-5">
            {entry.technicalNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SpacedRepetitionLogPage() {
  const latestTimestamp = releaseEntries[0]?.timestamp ?? new Date().toISOString();
  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-4xl space-y-10">
        <header className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Uxhulu Release Notes
      </p>
      <h1 className="text-3xl font-bold text-slate-900">Product Logbook</h1>
      <p className="text-sm text-slate-600 leading-relaxed">
        Every product-facing change lands here. The newest entry walks through
        the photo vocabulary import UX – from the upload choice to the
        AI-powered review screen and the dedicated vocabulary tab.
      </p>
      <div className="flex flex-wrap gap-3 text-sm text-slate-500">
        <span>Last update: {formatDateTime(latestTimestamp)}</span>
      </div>
        </header>

        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            <h2 className="text-xl font-semibold text-slate-900">
              Release Notes & Feature-Log
            </h2>
          </div>
          <p className="text-sm text-slate-600">
            Each release is recorded below. Use this log to understand what
            changed, why it matters, and how it impacts your learning flow.
          </p>
          <div className="space-y-4">
            {releaseEntries.map((entry) => (
              <ReleaseCard key={entry.id} {...entry} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/product_logbook")({
  component: SpacedRepetitionLogPage,
});
